import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Volume2, VolumeX, Info } from 'lucide-react';
import { FiTrash2 } from 'react-icons/fi';
import { useAuth } from '@lib/useAuth';
import { formatHoursMinutes } from '@lib/formatTime';
import { useUserRole } from '@lib/useUserRole';
import { supabase } from '@lib/supabase';
import {
  logDashboardEvent,
  type DashboardEventCategory,
  type DashboardEventValue,
} from '@lib/dashboardEvents';
import {
  startDashboardPageTimeEntry,
  completeDashboardPageTimeEntry,
  generateDashboardPageTimeId,
  type DashboardPageTimeExitType,
} from '@lib/dashboardPageTime';
import Button from '@components/Button';
import Modal from '@components/Modal';
import Container from '@components/Container';
import GradientDivider from '@components/GradientDivider';
import { listTemplatesForCurrentUser, createTemplate } from '@lib/templates';
import { listVisionBoardTemplates } from '@lib/visionBoardTemplates';
import { listBreathingRatios } from '@lib/breathingRatio';
import { listBreathingStyles } from '@lib/breathingStyles';
import { listBackgroundVideos } from '@lib/backgroundVideos';
import { listMusicTracks } from '@lib/musicTracks';
import {
  listAffirmationTypes,
  type AffirmationType as AffirmationTypeRecord,
} from '@lib/affirmations';
import QUICK_PRESETS, { type QuickPreset } from '@lib/quickPresets';
import { listQuotes } from '@lib/quotes';
import { calculateCurrentStreak } from '@lib/streaks';
import type {
  BackgroundVideo,
  MusicTrack,
  Quote,
  BreathingRatio,
  BreathingStyleRecord,
  VisionBoardImage,
  VisionBoardTemplate,
} from '@lib/api';
import type { SessionTemplate } from '@lib/api';
import {
  getUserSessions,
  getMostPopularBreathingRatioId,
  getMostPopularBackgroundVideoId,
  updateSessionNotes,
} from '@lib/sessions';
import type { BreathingSession as DbBreathingSession } from '@lib/sessions';
import {
  DEFAULT_BREATHING_STYLE,
  isBreathingStyle,
  type BreathingStyle,
} from '@/types/breathing';
import { useLeaderboardData } from '@lib/useLeaderboardData';
import {
  buildLeaderboardDisplayEntries,
  type LeaderboardDisplayRow,
  DEFAULT_LEADERBOARD_AVATAR,
} from '@lib/leaderboardDisplay';

// Extended session type that includes user data from joins
interface ExtendedSession extends DbBreathingSession {
  user_display_name?: string;
  user_name?: string;
  user_email?: string;
  user_settings?: {
    display_name?: string;
  };
}

interface BreathingStyleDisplayOption {
  id: string;
  value: BreathingStyle;
  label: string;
  description?: string | null;
}

type BreathPhase = 'inhale' | 'hold-in' | 'exhale' | 'hold-out';

const DEFAULT_CUSTOM_RATIO = ['4', '4', '4', '4'] as const;
const CUSTOM_RATIO_LABELS = ['Inhale', 'Hold', 'Exhale', 'Hold Out'] as const;
const DURATION_OPTIONS = [1, 3, 5, 10] as const;
type DurationOption = (typeof DURATION_OPTIONS)[number];

const BREATHING_STYLE_PRIORITY: Record<BreathingStyle, number> = {
  line: -1,
  circle: 1,
};

interface BadgeFeatureRequirementRow {
  badges?: {
    id?: string | null;
    name?: string | null;
    image_url?: string | null;
    color?: string | null;
  } | null;
}

type BadgeInfo = {
  id?: string | null;
  name?: string | null;
  image_url?: string | null;
  score?: number | null;
  color?: string | null;
};

interface LockedVideoModalInfo {
  entryKey: string;
  displayName: string;
  badgeObjects: BadgeInfo[];
  message: string;
}

// Dashboard-specific vision board row type that may include templates (which lack
// some stored fields like `user_id` and `filename`) and a local `is_template` flag.
type DashboardVisionBoard = Omit<VisionBoardImage, 'user_id' | 'filename'> & {
  user_id?: string;
  filename?: string;
  is_template?: boolean;
};

const LOCKED_OPTION_BASE_CLASSES =
  'cursor-pointer border border-amber-200/80 bg-amber-50/80 text-gray-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-100/90 hover:shadow-lg';

interface BackgroundVideoAccessRule {
  featureKey?: string;
  featureKeys?: string[];
  requiredBadge?: string;
  displayName: string;
  isFree?: boolean;
}

const BACKGROUND_VIDEO_RULES: Record<string, BackgroundVideoAccessRule> = {
  none: { isFree: true, displayName: 'None' },
  'sunset-field': { isFree: true, displayName: 'Sunset Field' },
  aurora: { isFree: true, displayName: 'Aurora Sky' },
  candle: { isFree: true, displayName: 'Candlelight Calm' },
  fireplace: { isFree: true, displayName: 'Fireplace Glow' },
  forest: {
    featureKeys: ['video_misty_forest', 'background_misty_forest'],
    requiredBadge: '30 Minutes Badge',
    displayName: 'Misty Forest',
  },
  mountain: {
    featureKeys: ['video_mountain_clouds', 'background_mountain_clouds'],
    requiredBadge: '30 Minutes Badge',
    displayName: 'Mountain Clouds',
  },
  'ocean-waves': {
    featureKeys: ['video_ocean_waves', 'background_ocean_waves'],
    requiredBadge: '3-Day Streak Badge',
    displayName: 'Ocean Waves',
  },
  starfield: {
    featureKeys: ['video_starfield_dream', 'background_starfield_dream'],
    requiredBadge: '3-Day Streak Badge',
    displayName: 'Starfield Dream',
  },
  underwater: {
    featureKeys: ['video_underwater_drift', 'background_underwater_drift'],
    requiredBadge: '2 Hours Badge',
    displayName: 'Underwater Drift',
  },
  'mars-horizon': {
    featureKeys: ['mars_horizon'],
    displayName: 'Mars Horizon',
  },
  'pyramid-serenity': {
    featureKeys: ['pyramid_serenity'],
    displayName: 'Pyramid Serenity',
  },
  'bora-bora-lagoon': {
    featureKeys: ['bora_bora_lagoon'],
    displayName: 'Bora Bora Lagoon',
  },
  'mt-fuji-reflection': {
    featureKeys: ['mt_fuji_reflection'],
    displayName: 'Mt Fuji Reflection',
  },
  'teddy-zen': {
    featureKeys: ['teddy_zen'],
    displayName: 'Teddy Zen',
  },
  'niagara-serenity': {
    featureKeys: ['niagara_serenity'],
    displayName: 'Niagara Serenity',
  },
  'cosmic-nebula': {
    featureKeys: ['cosmic_nebula'],
    displayName: 'Cosmic Nebula',
  },
  'heart-of-stillness': {
    featureKeys: ['heart_of_stillness'],
    displayName: 'Heart of Stillness',
  },
};

const BACKGROUND_VIDEO_ALIAS_MAP: Record<string, string> = {
  aurorasky: 'aurora',
  candlelightcalm: 'candle',
  fireplaceglow: 'fireplace',
  mistyforest: 'forest',
  mountainclouds: 'mountain',
  ocean: 'ocean-waves',
  oceanwaves: 'ocean-waves',
  starfielddream: 'starfield',
  underwaterdrift: 'underwater',
  marshorizon: 'mars-horizon',
  mars: 'mars-horizon',
  pyramidserenity: 'pyramid-serenity',
  pyramid: 'pyramid-serenity',
  borabora: 'bora-bora-lagoon',
  boraboralagoon: 'bora-bora-lagoon',
  bora: 'bora-bora-lagoon',
  mtfujireflection: 'mt-fuji-reflection',
  mtfuji: 'mt-fuji-reflection',
  teddyzen: 'teddy-zen',
  teddy: 'teddy-zen',
  niagaraserenity: 'niagara-serenity',
  niagara: 'niagara-serenity',
  cosmicnebula: 'cosmic-nebula',
  cosmic: 'cosmic-nebula',
  nebula: 'cosmic-nebula',
  sunsetfield: 'sunset-field',
  heartofstillness: 'heart-of-stillness',
};

const DEFAULT_COLOR_BACKGROUND = '#3BBAC3';
const DEFAULT_GRADIENT_BACKGROUND =
  'linear-gradient(135deg, #3BBAC3 0%, #598ED1 16%, #7660DE 32%, #9333EA 48%, #7F38E8 64%, #6C3EE7 80%, #5843E5 100%)';
const GRADIENT_BACKGROUND_PRESETS = [
  {
    id: 'unified-calm',
    label: 'Unified Calm Gradient',
    value: DEFAULT_GRADIENT_BACKGROUND,
  },
] as const;

const BACKGROUND_VIDEO_FEATURE_KEYS = Array.from(
  new Set(
    Object.values(BACKGROUND_VIDEO_RULES)
      .flatMap((rule) => [...(rule.featureKeys ?? []), rule.featureKey])
      .filter((key): key is string => Boolean(key))
  )
);

const FEATURE_REQUIREMENTS_TO_LOAD = Array.from(
  new Set(['custom_ratio', 'custom_time', ...BACKGROUND_VIDEO_FEATURE_KEYS])
);

const canonicalizeBackgroundVideoKey = (value: string): string => {
  const trimmed = (value || '').toLowerCase();
  if (!trimmed) return 'none';
  const normalized = trimmed.replace(/[^a-z0-9]+/g, '');
  return BACKGROUND_VIDEO_ALIAS_MAP[normalized] ?? trimmed;
};

const formatBadgeUnlockMessage = (badgeNames: string[]): string => {
  if (!badgeNames || badgeNames.length === 0) {
    return 'Earn the required badge to unlock this feature.';
  }

  if (badgeNames.length === 1) {
    return `Earn the "${badgeNames[0]}" badge to unlock this feature.`;
  }

  if (badgeNames.length === 2) {
    return `Earn the "${badgeNames[0]}" or "${badgeNames[1]}" badge to unlock this feature.`;
  }

  const formattedNames = badgeNames.map((name) => `"${name}"`);
  const lastBadge = formattedNames.pop();
  return `Earn one of these badges to unlock this feature: ${formattedNames.join(', ')}, or ${lastBadge}.`;
};

// If a badge name looks like a streak badge (contains a number and mentions
// days/streak), build the required sentence using the written word form of
// the number. Falls back to the original format for non-streak badges or
// when multiple badges are present.
const parseNumberFromBadgeName = (name?: string | null): number | null => {
  if (!name) return null;
  const m = name.match(/(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

const _TO_19 = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];

const _TENS = [
  '',
  '',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
];

const numberToWords = (n: number): string => {
  if (!Number.isFinite(n) || n < 0) return String(n);
  if (n < 20) return _TO_19[n] || String(n);
  if (n < 100) {
    const ten = Math.floor(n / 10);
    const rest = n % 10;
    const tensWord = _TENS[ten] ?? String(ten);
    const restWord = _TO_19[rest] ?? String(rest);
    return rest === 0 ? tensWord : `${tensWord}-${restWord}`;
  }
  if (n < 1000) {
    const h = Math.floor(n / 100);
    const rest = n % 100;
    return rest === 0
      ? `${_TO_19[h]} hundred`
      : `${_TO_19[h]} hundred ${numberToWords(rest)}`;
  }
  return String(n);
};

const formatLockedModalMessage = (badgeNames: string[]): string => {
  if (!badgeNames || badgeNames.length === 0) {
    return 'Earn the required badge to unlock this feature.';
  }

  // If there's only one badge and it's a streak badge, produce the special
  // sentence required by the product team.
  if (badgeNames.length === 1) {
    const badge = badgeNames[0] ?? '';
    const num = parseNumberFromBadgeName(badge);
    if (num !== null && /\bstreak|day(s)?\b/i.test(badge)) {
      const word = numberToWords(num);
      return `Complete guided breathing sessions ${word} ${num === 1 ? 'day' : 'days'} in a row to earn this badge. Once you unlock it, this feature becomes available to you.`;
    }

    // Time-based badges: "30 Minutes", "2 Hours" etc. — convert hours
    // to minutes and keep digits per requirement.
    if (
      num !== null &&
      /\b(minute|minutes|min|hour|hours|hr|hrs)\b/i.test(badge)
    ) {
      // If badge mentions hours convert to minutes.
      const isHour = /\bhour(s)?\b|\bhr(s)?\b/i.test(badge);
      const minutes = isHour ? num * 60 : num;
      return `Complete at least ${minutes} minutes of guided breathing to earn this badge. Once you unlock it, this feature becomes available to you.`;
    }
  }

  // Fallback to the existing formatting for other cases (multiple badges
  // or non-streak badges).
  return formatBadgeUnlockMessage(badgeNames);
};

const isDurationOption = (value: number): value is DurationOption =>
  (DURATION_OPTIONS as readonly number[]).includes(value);

const findPresetDurationForMinimum = (
  minimumSeconds: number
): DurationOption => {
  if (!Number.isFinite(minimumSeconds) || minimumSeconds <= 0) {
    return DURATION_OPTIONS[0];
  }

  const minutesNeeded = minimumSeconds / 60;
  const fallback =
    (DURATION_OPTIONS[DURATION_OPTIONS.length - 1] as DurationOption) ||
    DURATION_OPTIONS[0];
  return (
    (DURATION_OPTIONS.find((value) => value >= minutesNeeded) as
      | DurationOption
      | undefined) ?? fallback
  );
};

const parseRatioString = (ratio: string): number[] => {
  if (!ratio) return [];
  return ratio
    .split(/[:\\-xX]/)
    .map((part) => Number(part.trim()))
    .filter((value) => Number.isFinite(value) && value >= 0);
};

const formatSecondsLabel = (totalSeconds: number): string => {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return '0 seconds';
  }

  const roundedSeconds = Math.round(totalSeconds);
  if (roundedSeconds < 60) {
    return `${roundedSeconds} ${roundedSeconds === 1 ? 'second' : 'seconds'}`;
  }

  const minutes = Math.floor(roundedSeconds / 60);
  const seconds = roundedSeconds % 60;
  const parts: string[] = [];

  parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  if (seconds > 0) {
    parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
  }

  return parts.join(' ');
};

const formatMinutesInputValue = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '';

  const precision = minutes >= 10 ? 0 : minutes >= 1 ? 2 : 3;
  const factor = Math.pow(10, precision);
  const roundedUp = Math.ceil(minutes * factor) / factor;
  const formatted = roundedUp.toFixed(precision);
  return formatted.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
};

const formatMinutesSeconds = (
  totalSeconds: number | null | undefined
): string => {
  const s = Number(totalSeconds) || 0;
  if (s <= 0) return '0m 0s';
  const minutes = Math.floor(s / 60);
  const seconds = Math.round(s % 60);
  return `${minutes}m ${seconds}s`;
};

interface SessionDateParts {
  date: string;
  time: string;
}

function formatSessionDateWithTimezone(input: {
  created_at: string;
  user_timezone?: string | null;
}): SessionDateParts {
  const dt = new Date(input.created_at);
  if (Number.isNaN(dt.getTime())) return { date: '', time: '' };

  const timeZone = input.user_timezone || undefined;
  const dateOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone,
  } as const;
  const timeOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  } as const;

  try {
    const dateStr = new Intl.DateTimeFormat(undefined, dateOptions).format(dt);
    const timeStr = new Intl.DateTimeFormat(undefined, timeOptions).format(dt);
    return {
      date: `${dateStr},`,
      time: timeStr,
    };
  } catch {
    const fallbackDate = dt.toLocaleDateString(undefined, dateOptions);
    const fallbackTime = dt.toLocaleTimeString(undefined, timeOptions);
    return {
      date: `${fallbackDate},`,
      time: fallbackTime,
    };
  }
}

const DASHBOARD_PAGE_NAME = 'dashboard';
const VISITOR_ID_STORAGE_KEY = 'xhalin_visitor_id';
const SESSION_OPTIONS_STORAGE_KEY = 'xhalin_session_options';

interface ActiveDashboardPageTimeEntry {
  id: string;
  enteredAtIso: string;
  startedAtMs: number;
}

const generateDashboardSessionId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.random() * 16;
    const value =
      char === 'x' ? Math.floor(rand) : (Math.floor(rand) & 0x3) | 0x8;
    return value.toString(16);
  });
};

const isValidUuid = (value: string | null): value is string => {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );
};

const getOrCreateVisitorId = (): string => {
  if (typeof window === 'undefined') {
    return generateDashboardSessionId();
  }

  try {
    const existing = window.localStorage.getItem(VISITOR_ID_STORAGE_KEY);
    if (isValidUuid(existing)) {
      return existing;
    }
    const next = generateDashboardSessionId();
    window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, next);
    return next;
  } catch {
    return generateDashboardSessionId();
  }
};

function Dashboard() {
  const navigate = useNavigate();

  const [savedOptions] = useState(() => {
    try {
      const saved = localStorage.getItem(SESSION_OPTIONS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [selectedRatio, setSelectedRatio] = useState<string>(
    savedOptions.selectedRatio ?? ''
  );
  const [isBreathing, setIsBreathing] = useState(false);
  const [isFullMode, setIsFullMode] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<BreathPhase>('inhale');
  const [phaseTimeLeft, setPhaseTimeLeft] = useState(4);
  const timerRef = useRef<number | null>(null);
  const minimumDurationRef = useRef<number>(0);
  const visitorIdRef = useRef<string>('');
  const dashboardSessionIdRef = useRef<string>('');
  const activePageTimeEntryRef = useRef<ActiveDashboardPageTimeEntry | null>(
    null
  );
  const pageTimeInitializedRef = useRef(false);
  const pageTimeSessionClosedRef = useRef(false);
  const pageTimeStartPromiseRef = useRef<Promise<void> | null>(null);
  const pendingTabHiddenTimeoutRef = useRef<number | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(
    savedOptions.selectedDuration ?? 5
  );
  const [isCustomDuration, setIsCustomDuration] = useState<boolean>(
    savedOptions.isCustomDuration ?? false
  );
  const [customDuration, setCustomDuration] = useState<string>(
    savedOptions.customDuration ?? ''
  );
  const [voiceGuidance, setVoiceGuidance] = useState<boolean>(
    savedOptions.voiceGuidance ?? true
  );
  const [backgroundSound, setBackgroundSound] = useState<string>(
    savedOptions.backgroundSound ?? 'on'
  );
  const [phaseChime, setPhaseChime] = useState<boolean>(
    savedOptions.phaseChime ?? true
  );
  const [introVoice, setIntroVoice] = useState<boolean>(
    savedOptions.introVoice ?? true
  );
  const [playInBackground, setPlayInBackground] = useState<boolean>(
    savedOptions.playInBackground ?? true
  );
  const [hideCycleCount, setHideCycleCount] = useState<boolean>(
    savedOptions.hideCycleCount ?? false
  );
  const [hideTimeLeft, setHideTimeLeft] = useState<boolean>(
    savedOptions.hideTimeLeft ?? false
  );
  const [musicEnabled, setMusicEnabled] = useState<boolean>(
    savedOptions.musicEnabled ?? false
  );
  const [musicTracks, setMusicTracks] = useState<MusicTrack[]>([]);
  const [selectedMusicTrackId, setSelectedMusicTrackId] = useState<
    string | null
  >(savedOptions.selectedMusicTrackId ?? null);
  const [playingMusicPreview, setPlayingMusicPreview] = useState<string | null>(
    null
  );
  const musicPreviewAudioRef = useRef<HTMLAudioElement | null>(null);

  const [affirmationTypesFromDb, setAffirmationTypesFromDb] = useState<
    AffirmationTypeRecord[]
  >([]);

  useEffect(() => {
    return () => {
      if (musicPreviewAudioRef.current) {
        try {
          musicPreviewAudioRef.current.pause();
          musicPreviewAudioRef.current.currentTime = 0;
          musicPreviewAudioRef.current.src = '';
          musicPreviewAudioRef.current.load();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await listAffirmationTypes();
        if (active) {
          setAffirmationTypesFromDb(list || []);
        }
      } catch (error) {
        if (active) {
          console.error('Failed to load affirmation types', error);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const RESERVED_AFFIRMATION_TYPE_IDS = new Set([
    'regular',
    'whisper',
    'classic',
  ]);

  const normalizeAffirmationTypeValue = (value?: string | null) => {
    const raw = (value ?? '').trim();
    if (!raw) return DEFAULT_AFFIRMATION_TYPE;
    if (RESERVED_AFFIRMATION_TYPE_IDS.has(raw.toLowerCase())) {
      return DEFAULT_AFFIRMATION_TYPE;
    }
    return raw;
  };

  const AFFIRMATION_TYPE_OPTIONS = useMemo(() => {
    if (!affirmationTypesFromDb || affirmationTypesFromDb.length === 0) {
      return [
        { value: 'panic_attack_relief', label: 'Panic Attack Relief' },
        { value: 'calm_anxiety', label: 'Calm & Anxiety Relief' },
        { value: 'confidence_selfworth', label: 'Confidence & Self-Worth' },
        { value: 'money_abundance', label: 'Money & Abundance' },
        { value: 'love_relationships', label: 'Love & Relationships' },
        { value: 'health_healing', label: 'Health & Healing' },
        { value: 'focus_clarity', label: 'Focus & Clarity' },
        { value: 'gratitude', label: 'Gratitude' },
        { value: 'sleep_deep_rest', label: 'Sleep & Deep Rest' },
      ];
    }
    return affirmationTypesFromDb
      .filter((t) => t.active)
      .filter((t) => !RESERVED_AFFIRMATION_TYPE_IDS.has(t.id.toLowerCase()))
      .map((t) => ({
        value: t.id,
        label: t.name,
      }));
  }, [affirmationTypesFromDb]);

  type AffirmationType = string;
  const DEFAULT_AFFIRMATION_TYPE = 'calm_anxiety';
  const [affirmations, setAffirmations] = useState<boolean>(
    savedOptions.affirmations ?? false
  );
  const [affirmationType, setAffirmationType] = useState<AffirmationType | ''>(
    normalizeAffirmationTypeValue(savedOptions.affirmationType)
  );

  // Migration: If the saved affirmationType is a legacy label, map it to the correct ID once DB records load.
  useEffect(() => {
    if (affirmationTypesFromDb.length === 0 || !affirmationType) return;

    // Check if the current value exists as an ID
    const exists = affirmationTypesFromDb.some((t) => t.id === affirmationType);
    if (exists) return;

    // If not, try to find a record whose name matches (case-insensitive, normalized) or normalized ID matches
    const normalizedCurrent = affirmationType
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');

    const found = affirmationTypesFromDb.find((t) => {
      const normalizedName = t.name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      return t.id === normalizedCurrent || normalizedName === normalizedCurrent;
    });

    if (found) {
      setAffirmationType(found.id);
    }
  }, [affirmationTypesFromDb, affirmationType]);

  const [affirmationWhisperMode, setAffirmationWhisperMode] = useState<boolean>(
    savedOptions.affirmationWhisperMode ?? false
  );
  const [affirmationMode, setAffirmationMode] = useState<
    'regular' | 'subliminal'
  >(savedOptions.affirmationMode ?? 'regular');

  // Quick presets state for Start Your Session
  const [selectedQuickPresetId, setSelectedQuickPresetId] = useState<string>(
    savedOptions.selectedQuickPresetId ?? ''
  );
  const selectedQuickPreset = useMemo(() => {
    return QUICK_PRESETS.find((p) => p.id === selectedQuickPresetId) ?? null;
  }, [selectedQuickPresetId]);
  const [breathingStyle, setBreathingStyle] = useState<BreathingStyle>(
    savedOptions.breathingStyle ?? 'line'
  );
  const [breathingStylesFromDb, setBreathingStylesFromDb] = useState<
    BreathingStyleRecord[]
  >([]);
  const [backgroundVideo, setBackgroundVideo] = useState<string>(
    savedOptions.backgroundVideo ?? 'none'
  );
  const [hasTouchedBackgroundVideo, setHasTouchedBackgroundVideo] = useState(
    savedOptions.hasTouchedBackgroundVideo ?? false
  );
  const [colorBackgroundMode, setColorBackgroundMode] = useState<
    'custom' | 'gradient'
  >(savedOptions.colorBackgroundMode ?? 'custom');
  const [backgroundColor, setBackgroundColor] = useState<string>(
    savedOptions.backgroundColor ?? DEFAULT_COLOR_BACKGROUND
  );
  const [isColorBackgroundSelected, setIsColorBackgroundSelected] =
    useState<boolean>(savedOptions.isColorBackgroundSelected ?? false);
  const [backgroundGradient, setBackgroundGradient] = useState<string>(
    savedOptions.backgroundGradient ?? DEFAULT_GRADIENT_BACKGROUND
  );
  const [isGradientBackgroundSelected, setIsGradientBackgroundSelected] =
    useState<boolean>(savedOptions.isGradientBackgroundSelected ?? false);
  const [visionBoardImages, setVisionBoardImages] = useState<
    DashboardVisionBoard[]
  >([]);
  const [selectedVisionBoardImage, setSelectedVisionBoardImage] =
    useState<DashboardVisionBoard | null>(null);

  // Delete flow for Vision Boards (allows deleting right from the chooser)
  const [activeVisionBoardToDelete, setActiveVisionBoardToDelete] =
    useState<DashboardVisionBoard | null>(null);
  const [isDeletingVisionBoard, setIsDeletingVisionBoard] = useState(false);
  const [deleteVisionBoardError, setDeleteVisionBoardError] = useState<
    string | null
  >(null);

  const [breathingRatiosFromDb, setBreathingRatiosFromDb] = useState<
    BreathingRatio[]
  >([]);
  // '' = no selection, 'custom' = other/custom, otherwise stores a breathing ratio id
  const [selectedBreathingRatioId, setSelectedBreathingRatioId] =
    useState<string>(savedOptions.selectedBreathingRatioId ?? '');
  const [hasUserSelectedBreathingRatio, setHasUserSelectedBreathingRatio] =
    useState(savedOptions.hasUserSelectedBreathingRatio ?? false);
  const [selectedBackgroundVideoId, setSelectedBackgroundVideoId] = useState<
    string | null
  >(savedOptions.selectedBackgroundVideoId ?? null);
  const [customRatioParts, setCustomRatioParts] = useState<string[]>(
    savedOptions.customRatioParts ?? [...DEFAULT_CUSTOM_RATIO]
  );
  const [showCustomRatioUnlockModal, setShowCustomRatioUnlockModal] =
    useState(false);
  const [showCustomTimeLockedModal, setShowCustomTimeLockedModal] =
    useState(false);
  const [previewBadge, setPreviewBadge] = useState<BadgeInfo | null>(null);
  const [featureBadgeRequirements, setFeatureBadgeRequirements] = useState<
    Record<string, BadgeInfo[]>
  >({});
  const [lockedVideoModalInfo, setLockedVideoModalInfo] =
    useState<LockedVideoModalInfo | null>(null);
  // Template state
  const [templates, setTemplates] = useState<SessionTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('');
  const [isSavingTemplate, setIsSavingTemplate] = useState<boolean>(false);
  // When true the user clicked the helper link and we should enable sound
  // automatically after they pick a background (but not for 'none').
  const [wishToEnableBackgroundSound, setWishToEnableBackgroundSound] =
    useState<boolean>(false);

  // Recent sessions loaded from the database (limited to latest few for dashboard)
  const [recentSessionsFromDb, setRecentSessionsFromDb] = useState<
    ExtendedSession[]
  >([]);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [allSessionsFromDb, setAllSessionsFromDb] = useState<ExtendedSession[]>(
    []
  );
  const [mostPopularBreathingRatioId, setMostPopularBreathingRatioId] =
    useState<string | null>(null);
  const [mostPopularBackgroundVideoId, setMostPopularBackgroundVideoId] =
    useState<string | null>(null);

  const breathingRatioOptions = useMemo(() => {
    if (!breathingRatiosFromDb || breathingRatiosFromDb.length === 0) {
      return [];
    }

    if (!mostPopularBreathingRatioId) {
      return breathingRatiosFromDb;
    }

    const popularRatio = breathingRatiosFromDb.find(
      (ratio) => ratio.id === mostPopularBreathingRatioId
    );
    if (!popularRatio) {
      return breathingRatiosFromDb;
    }

    const rest = breathingRatiosFromDb.filter(
      (ratio) => ratio.id !== mostPopularBreathingRatioId
    );
    return [popularRatio, ...rest];
  }, [breathingRatiosFromDb, mostPopularBreathingRatioId]);

  const { user, userFeatures } = useAuth();
  const { role } = useUserRole(user?.id ?? null);
  const isAdmin = role === 'admin';
  const latestUserIdRef = useRef<string | null>(user?.id ?? null);

  useEffect(() => {
    latestUserIdRef.current = user?.id ?? null;
  }, [user?.id]);

  useEffect(() => {
    const options = {
      selectedRatio,
      selectedDuration,
      isCustomDuration,
      customDuration,
      voiceGuidance,
      backgroundSound,
      phaseChime,
      introVoice,
      playInBackground,
      hideCycleCount,
      hideTimeLeft,
      musicEnabled,
      selectedMusicTrackId,
      affirmations,
      affirmationType,
      affirmationWhisperMode,
      affirmationMode,
      breathingStyle,
      backgroundVideo,
      hasTouchedBackgroundVideo,
      colorBackgroundMode,
      backgroundColor,
      isColorBackgroundSelected,
      backgroundGradient,
      isGradientBackgroundSelected,
      selectedBreathingRatioId,
      hasUserSelectedBreathingRatio,
      selectedBackgroundVideoId,
      customRatioParts,
      selectedQuickPresetId,
      selectedVisionBoardImageId: selectedVisionBoardImage?.id || null,
    };
    localStorage.setItem(SESSION_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [
    selectedRatio,
    selectedDuration,
    isCustomDuration,
    customDuration,
    voiceGuidance,
    backgroundSound,
    phaseChime,
    introVoice,
    playInBackground,
    hideCycleCount,
    hideTimeLeft,
    musicEnabled,
    selectedMusicTrackId,
    affirmations,
    affirmationType,
    affirmationWhisperMode,
    affirmationMode,
    breathingStyle,
    backgroundVideo,
    hasTouchedBackgroundVideo,
    colorBackgroundMode,
    backgroundColor,
    isColorBackgroundSelected,
    backgroundGradient,
    isGradientBackgroundSelected,
    selectedBreathingRatioId,
    hasUserSelectedBreathingRatio,
    selectedBackgroundVideoId,
    customRatioParts,
    selectedQuickPresetId,
    selectedVisionBoardImage,
  ]);

  useEffect(() => {
    if (
      visionBoardImages.length > 0 &&
      !selectedVisionBoardImage &&
      savedOptions?.selectedVisionBoardImageId
    ) {
      const found = visionBoardImages.find(
        (img) => img.id === savedOptions.selectedVisionBoardImageId
      );
      if (found) {
        setSelectedVisionBoardImage(found);
      }
    }
  }, [
    visionBoardImages,
    selectedVisionBoardImage,
    savedOptions?.selectedVisionBoardImageId,
  ]);

  if (!dashboardSessionIdRef.current) {
    dashboardSessionIdRef.current = generateDashboardSessionId();
  }
  if (!visitorIdRef.current) {
    visitorIdRef.current = getOrCreateVisitorId();
  }

  const logDashboardInteraction = useCallback(
    (event: {
      eventType: string;
      eventCategory: DashboardEventCategory | string;
      eventLabel?: string | null;
      eventValue?: DashboardEventValue;
    }) => {
      logDashboardEvent({
        ...event,
        visitorId: visitorIdRef.current,
        sessionId: dashboardSessionIdRef.current,
        userId: user?.id ?? null,
        page: DASHBOARD_PAGE_NAME,
      }).catch((error) => {
        if (import.meta.env.DEV) {
          console.error('Failed to log dashboard event', error);
        }
      });
    },
    [user?.id]
  );

  const resolveNavigationExitType =
    useCallback((): DashboardPageTimeExitType => {
      if (
        typeof performance !== 'undefined' &&
        typeof performance.getEntriesByType === 'function'
      ) {
        const entries = performance.getEntriesByType(
          'navigation'
        ) as PerformanceNavigationTiming[];
        const latestEntry = entries?.[entries.length - 1];
        if (latestEntry && latestEntry.type === 'reload') {
          return 'refresh';
        }
      }

      return 'unload';
    }, []);

  const startPageTimeTracking = useCallback(() => {
    if (
      pageTimeSessionClosedRef.current ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    if (activePageTimeEntryRef.current) {
      return;
    }

    const entryId = generateDashboardPageTimeId();
    const now = new Date();
    const entry: ActiveDashboardPageTimeEntry = {
      id: entryId,
      enteredAtIso: now.toISOString(),
      startedAtMs: now.getTime(),
    };

    activePageTimeEntryRef.current = entry;

    const promise = startDashboardPageTimeEntry({
      id: entryId,
      visitorId: visitorIdRef.current,
      sessionId: dashboardSessionIdRef.current,
      userId: latestUserIdRef.current,
      page: DASHBOARD_PAGE_NAME,
      enteredAt: entry.enteredAtIso,
    }).catch((error) => {
      if (import.meta.env.DEV) {
        console.error('[dashboard] failed to start page timing', error);
      }
    });

    pageTimeStartPromiseRef.current = promise;
    promise.finally(() => {
      if (pageTimeStartPromiseRef.current === promise) {
        pageTimeStartPromiseRef.current = null;
      }
    });
  }, []);

  const completePageTimeTracking = useCallback(
    (
      exitType: DashboardPageTimeExitType,
      options?: { allowRestart?: boolean }
    ) => {
      const entry = activePageTimeEntryRef.current;
      if (!entry) {
        if (!options?.allowRestart) {
          pageTimeSessionClosedRef.current = true;
        }
        return;
      }

      activePageTimeEntryRef.current = null;

      const finalize = async () => {
        if (pageTimeStartPromiseRef.current) {
          try {
            await pageTimeStartPromiseRef.current;
          } catch {
            // ignore start failures on completion
          }
        }

        const now = new Date();
        const durationMs = Math.max(0, now.getTime() - entry.startedAtMs);
        try {
          await completeDashboardPageTimeEntry({
            id: entry.id,
            visitorId: visitorIdRef.current,
            sessionId: dashboardSessionIdRef.current,
            userId: latestUserIdRef.current,
            page: DASHBOARD_PAGE_NAME,
            enteredAt: entry.enteredAtIso,
            exitedAt: now.toISOString(),
            durationSeconds: durationMs / 1000,
            exitType,
          });
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[dashboard] failed to complete page timing', error);
          }
        }
      };

      void finalize();

      if (!options?.allowRestart) {
        pageTimeSessionClosedRef.current = true;
      }
    },
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (pageTimeInitializedRef.current) {
      return;
    }

    pageTimeInitializedRef.current = true;
    startPageTimeTracking();

    const clearPendingTabHiddenTimeout = () => {
      if (pendingTabHiddenTimeoutRef.current !== null) {
        window.clearTimeout(pendingTabHiddenTimeoutRef.current);
        pendingTabHiddenTimeoutRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (pageTimeSessionClosedRef.current) return;
        clearPendingTabHiddenTimeout();
        pendingTabHiddenTimeoutRef.current = window.setTimeout(() => {
          pendingTabHiddenTimeoutRef.current = null;
          if (!pageTimeSessionClosedRef.current) {
            completePageTimeTracking('tab_hidden', { allowRestart: true });
          }
        }, 150);
      } else if (document.visibilityState === 'visible') {
        clearPendingTabHiddenTimeout();
        if (
          !pageTimeSessionClosedRef.current &&
          !activePageTimeEntryRef.current
        ) {
          startPageTimeTracking();
        }
      }
    };

    const handleBeforeUnload = () => {
      clearPendingTabHiddenTimeout();
      completePageTimeTracking(resolveNavigationExitType(), {
        allowRestart: false,
      });
    };

    const handlePageHide = () => {
      clearPendingTabHiddenTimeout();
      completePageTimeTracking(resolveNavigationExitType(), {
        allowRestart: false,
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearPendingTabHiddenTimeout();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      completePageTimeTracking('route_change', { allowRestart: false });
    };
  }, [
    completePageTimeTracking,
    resolveNavigationExitType,
    startPageTimeTracking,
  ]);

  const logDropdownOpen = useCallback(
    (category: DashboardEventCategory | string) => {
      logDashboardInteraction({
        eventType: 'dropdown_open',
        eventCategory: category,
      });
    },
    [logDashboardInteraction]
  );

  const logOptionSelect = useCallback(
    (
      category: DashboardEventCategory | string,
      label?: string | null,
      value?: DashboardEventValue
    ) => {
      logDashboardInteraction({
        eventType: 'option_select',
        eventCategory: category,
        eventLabel: label ?? null,
        eventValue: value,
      });
    },
    [logDashboardInteraction]
  );

  const logToggleEvent = useCallback(
    (
      category: DashboardEventCategory | string,
      label: string,
      value?: DashboardEventValue
    ) => {
      logDashboardInteraction({
        eventType: 'toggle',
        eventCategory: category,
        eventLabel: label,
        eventValue: value,
      });
    },
    [logDashboardInteraction]
  );

  // Admin users should have access to everything — treat admin as having
  // all feature flags enabled so they bypass product locks while testing.
  const hasCustomRatioFeature =
    userFeatures.includes('custom_ratio') || isAdmin;
  const hasCustomTimeFeature = userFeatures.includes('custom_time') || isAdmin;
  const canUseBackgroundVideo = useCallback(
    (key: string) => {
      if (isAdmin) return true;
      const canonical = canonicalizeBackgroundVideoKey(key);
      const rule = BACKGROUND_VIDEO_RULES[canonical];
      if (!rule) return true;
      if (rule.isFree) return true;
      const featureKeys =
        rule.featureKeys ??
        (rule.featureKey ? [rule.featureKey] : undefined) ??
        [];
      if (featureKeys.length === 0) return true;
      return featureKeys.some((featureKey) =>
        userFeatures.includes(featureKey)
      );
    },
    [userFeatures, isAdmin]
  );

  useEffect(() => {
    if (hasCustomRatioFeature) return;
    if (selectedBreathingRatioId !== 'custom') return;
    const fallback = breathingRatiosFromDb[0];
    if (fallback) {
      setSelectedBreathingRatioId(fallback.id);
      setSelectedRatio(fallback.ratio);
    } else {
      setSelectedBreathingRatioId('');
      setSelectedRatio('');
    }
    setHasUserSelectedBreathingRatio(false);
  }, [hasCustomRatioFeature, selectedBreathingRatioId, breathingRatiosFromDb]);

  useEffect(() => {
    let active = true;
    async function loadFeatureRequirements(featureKey: string) {
      try {
        const { data: featureRow, error: featureError } = await supabase
          .from('features')
          .select('id')
          .eq('key', featureKey)
          .maybeSingle();

        if (featureError) throw featureError;
        if (!featureRow?.id) {
          if (active) {
            setFeatureBadgeRequirements((prev) => ({
              ...prev,
              [featureKey]: [],
            }));
          }
          return;
        }

        const { data: badgeRows, error: badgeError } = await supabase
          .from('badge_features')
          .select(
            `
              badges (
                id,
                name,
                image_url,
                color,
                score
              )
            `
          )
          .eq('feature_id', featureRow.id);

        if (badgeError) throw badgeError;

        const rows =
          (badgeRows as BadgeFeatureRequirementRow[] | null | undefined)
            ?.map((row) => row?.badges)
            .filter(
              (
                b
              ): b is {
                id?: string | null;
                name?: string | null;
                image_url?: string | null;
                color?: string | null;
              } => Boolean(b)
            ) ?? [];

        if (active) {
          setFeatureBadgeRequirements((prev) => ({
            ...prev,
            [featureKey]: rows,
          }));
        }
      } catch (err) {
        console.debug(
          'Failed to load feature requirements for',
          featureKey,
          err
        );
        if (active) {
          setFeatureBadgeRequirements((prev) => ({
            ...prev,
            [featureKey]: [],
          }));
        }
      }
    }

    FEATURE_REQUIREMENTS_TO_LOAD.forEach((featureKey) => {
      loadFeatureRequirements(featureKey);
    });
    return () => {
      active = false;
    };
  }, []);

  const breathingStyleDisplayOptions = useMemo(() => {
    const options = breathingStylesFromDb
      .map((style) => {
        const normalizedName = style.name?.trim().toLowerCase();
        if (!normalizedName || !isBreathingStyle(normalizedName)) {
          return null as null;
        }
        return {
          id: style.id,
          value: normalizedName as BreathingStyle,
          label: style.name || '',
          description: style.description ?? null,
        } as BreathingStyleDisplayOption;
      })
      .filter(
        (option): option is BreathingStyleDisplayOption => option !== null
      );

    return [...options].sort(
      (a, b) =>
        BREATHING_STYLE_PRIORITY[a.value] - BREATHING_STYLE_PRIORITY[b.value]
    );
  }, [breathingStylesFromDb]);

  useEffect(() => {
    (async () => {
      if (!user) return;
      try {
        const list = await getUserSessions(user.id, 5);
        setRecentSessionsFromDb(list || []);
      } catch (e) {
        console.error('Failed to load recent sessions', e);
      }
    })();
  }, [user]);

  useEffect(() => {
    let isMounted = true;

    // reset loading flag for session stats (no-op after removing flag)

    (async () => {
      if (!user) {
        if (isMounted) {
          setAllSessionsFromDb([]);
        }
        return;
      }
      try {
        const list = await getUserSessions(user.id);
        if (isMounted) {
          setAllSessionsFromDb(list || []);
        }
      } catch (e) {
        if (isMounted) {
          console.error('Failed to load session history', e);
        }
      } finally {
        if (isMounted) {
          // session load finished
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const [leaderboardTab, setLeaderboardTab] = useState<'minutes' | 'streak'>(
    'minutes'
  );

  const {
    entries: leaderboardEntries,
    profiles: leaderboardProfiles,
    loading: isLoadingLeaderboard,
    error: leaderboardError,
  } = useLeaderboardData(25);

  const displayedLeaderboard = useMemo<LeaderboardDisplayRow[]>(
    () =>
      buildLeaderboardDisplayEntries({
        entries: leaderboardEntries,
        profiles: leaderboardProfiles,
        leaderboardTab,
        currentUserId: user?.id ?? null,
        limit: 10,
      }),
    [leaderboardEntries, leaderboardProfiles, leaderboardTab, user?.id]
  );

  // Small preview mapping for background videos. Using emoji placeholders
  // so previews work even if no local thumbnail assets are available.
  const videoPreviews: Record<
    string,
    { emoji: string; label: string; alt?: string }
  > = {
    none: { emoji: '⬜', label: 'None' },
    starfield: { emoji: '✨', label: 'Starfield Dream' },
    fireplace: { emoji: '🔥', label: 'Fireplace Glow' },
    mountain: { emoji: '🏔️', label: 'Mountain Clouds' },
    forest: { emoji: '🌲', label: 'Misty Forest' },
    aurora: { emoji: '🌌', label: 'Aurora Sky' },
    candle: { emoji: '🕯️', label: 'Candlelight Calm' },
    'sunset-field': { emoji: '🌇', label: 'Sunset Field' },
    underwater: { emoji: '🐠', label: 'Underwater Drift' },
    ocean: { emoji: '🌊', label: 'Ocean Waves' },
    'ocean-waves': { emoji: '🌊', label: 'Ocean Waves' },
    'mars-horizon': { emoji: '🪐', label: 'Mars Horizon' },
    'pyramid-serenity': { emoji: '🔺', label: 'Pyramid Serenity' },
    'bora-bora-lagoon': { emoji: '🏝️', label: 'Bora Bora Lagoon' },
    'mt-fuji-reflection': { emoji: '🗻', label: 'Mt Fuji Reflection' },
    'teddy-zen': { emoji: '🧸', label: 'Teddy Zen' },
    'niagara-serenity': { emoji: '🌊', label: 'Niagara Serenity' },
    'cosmic-nebula': { emoji: '🌌', label: 'Cosmic Nebula' },
  };

  // helper: map preview keys to emoji/labels (used directly via `videoPreviews`)

  // Track failed image loads so we can fall back to emoji previews
  const [failedPreviews, setFailedPreviews] = useState<Record<string, boolean>>(
    {}
  );

  // Background videos loaded from the database
  const [backgroundVideosFromDb, setBackgroundVideosFromDb] = useState<
    BackgroundVideo[]
  >([]);

  // Inspirational quote loaded from DB (only active quotes)
  const [activeQuote, setActiveQuote] = useState<Quote | null>(null);

  const keyFromFileName = (fileName: string) => {
    if (!fileName) return '';
    // strip extension and any path
    const base = fileName.split('/').pop() || fileName;
    return base.replace(/\.[^.]+$/, '');
  };

  const buildBadgeAssetUrl = (value?: string | null) => {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (trimmed.startsWith('http')) return trimmed;
    const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').replace(
      /\/$/,
      ''
    );
    const normalizedPath = trimmed.replace(/^\/+/, '');
    return `${supabaseUrl}/storage/v1/object/public/${encodeURI(normalizedPath)}`;
  };

  const mostPopularBackgroundVideoKey = useMemo(() => {
    if (!mostPopularBackgroundVideoId) return null;
    const found = backgroundVideosFromDb.find(
      (video) => video.id === mostPopularBackgroundVideoId
    );
    if (!found) return null;
    return keyFromFileName(found.file_name);
  }, [mostPopularBackgroundVideoId, backgroundVideosFromDb]);

  const hasSelectedBackground = useMemo(() => {
    if (isColorBackgroundSelected) return true;
    if (isGradientBackgroundSelected) return true;
    if (selectedVisionBoardImage) return true;
    return backgroundVideo !== 'none';
  }, [
    backgroundVideo,
    selectedVisionBoardImage,
    isColorBackgroundSelected,
    isGradientBackgroundSelected,
  ]);

  // Ensure background sound cannot be on when no background is selected.
  // Skip enforcement on the first render so initial default 'on' remains visible
  // to the user (we still enforce on subsequent changes).
  const _bgSoundEnforceRef = useRef<boolean>(false);
  useEffect(() => {
    if (!_bgSoundEnforceRef.current) {
      _bgSoundEnforceRef.current = true;
      return;
    }

    // Only auto-disable background sound when the user has interacted with
    // the background chooser (or applied a template). This keeps the default
    // state as 'on' for new users while preventing accidental sound when the
    // user explicitly selects "None".
    if (!hasTouchedBackgroundVideo) return;

    if (!hasSelectedBackground && backgroundSound === 'on') {
      setBackgroundSound('off');
    }
  }, [backgroundSound, hasSelectedBackground, hasTouchedBackgroundVideo]);

  // Load active breathing ratios from the DB
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const list = await listBreathingRatios();
        if (!active) return;
        setBreathingRatiosFromDb(list || []);
      } catch (error) {
        if (active) {
          console.error('Failed to load breathing ratios', error);
          setBreathingRatiosFromDb([]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!breathingRatiosFromDb || breathingRatiosFromDb.length === 0) {
      setMostPopularBreathingRatioId(null);
      return () => {
        active = false;
      };
    }

    (async () => {
      const ids = breathingRatiosFromDb.map((ratio) => ratio.id);
      const topId = await getMostPopularBreathingRatioId(ids);
      if (!active) return;
      setMostPopularBreathingRatioId(topId);
    })();

    return () => {
      active = false;
    };
  }, [breathingRatiosFromDb]);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const list = await listBreathingStyles();
        if (!active) return;
        setBreathingStylesFromDb(list || []);
      } catch (error) {
        if (active) {
          console.error('Failed to load breathing styles', error);
          setBreathingStylesFromDb([]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  // Load active music tracks from DB
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await listMusicTracks();
        if (active) {
          setMusicTracks(list || []);
        }
      } catch (error) {
        if (active) {
          console.error('Failed to load music tracks', error);
          setMusicTracks([]);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Ensure we have a selected ratio once data is available. Prefer the
  // most-popular ratio when it becomes known, but avoid overwriting an
  // explicit user selection.
  useEffect(() => {
    if (breathingRatiosFromDb.length === 0) return;

    const popular = mostPopularBreathingRatioId
      ? breathingRatiosFromDb.find(
          (ratio) => ratio.id === mostPopularBreathingRatioId
        )
      : null;

    // If nothing selected yet, choose popular if present, otherwise first.
    if (!selectedBreathingRatioId) {
      const chosen = popular || breathingRatiosFromDb[0];
      if (chosen) {
        setSelectedBreathingRatioId(chosen.id);
        setSelectedRatio(chosen.ratio);
      }
      return;
    }

    // If we previously auto-chosen the first item as a fallback and the
    // true most-popular value becomes available, upgrade the selection to
    // the most-popular one. Do not override if the user already picked a
    // different option.
    if (
      popular &&
      !hasUserSelectedBreathingRatio &&
      selectedBreathingRatioId === (breathingRatiosFromDb[0]?.id ?? '')
    ) {
      setSelectedBreathingRatioId(popular.id);
      setSelectedRatio(popular.ratio);
    }
  }, [
    breathingRatiosFromDb,
    selectedBreathingRatioId,
    mostPopularBreathingRatioId,
    hasUserSelectedBreathingRatio,
  ]);

  // When breathing ratios load or the selectedBreathingRatioId changes,
  // ensure selectedRatio reflects the chosen id if possible.
  useEffect(() => {
    if (
      selectedBreathingRatioId &&
      selectedBreathingRatioId !== 'custom' &&
      breathingRatiosFromDb.length > 0
    ) {
      const found = breathingRatiosFromDb.find(
        (r) => r.id === selectedBreathingRatioId
      );
      if (found) {
        setSelectedRatio(found.ratio);
      } else {
        const fallbackRatio = breathingRatiosFromDb[0];
        if (fallbackRatio) {
          setSelectedBreathingRatioId(fallbackRatio.id);
          setSelectedRatio(fallbackRatio.ratio);
          setHasUserSelectedBreathingRatio(false);
        }
      }
    }
  }, [selectedBreathingRatioId, breathingRatiosFromDb]);

  const activeBreathingRatio = useMemo(() => {
    if (selectedBreathingRatioId && selectedBreathingRatioId !== 'custom') {
      const fromId = breathingRatiosFromDb.find(
        (r) => r.id === selectedBreathingRatioId
      );
      if (fromId) return fromId;
    }
    return breathingRatiosFromDb.find((r) => r.ratio === selectedRatio) || null;
  }, [selectedBreathingRatioId, breathingRatiosFromDb, selectedRatio]);

  const customRatioSelected =
    hasCustomRatioFeature && selectedBreathingRatioId === 'custom';
  const isUsingCustomRatio = customRatioSelected;
  const customRatioRequiredBadgeObjects =
    featureBadgeRequirements.custom_ratio || [];
  const customTimeRequiredBadgeObjects =
    featureBadgeRequirements.custom_time || [];

  const customRatioRequiredBadges = customRatioRequiredBadgeObjects
    .map((b) => b?.name)
    .filter((n): n is string => Boolean(n));

  const customTimeRequiredBadges = customTimeRequiredBadgeObjects
    .map((b) => b?.name)
    .filter((n): n is string => Boolean(n));
  const customRatioUnlockMessage = formatLockedModalMessage(
    customRatioRequiredBadges
  );
  const customTimeUnlockMessage = formatLockedModalMessage(
    customTimeRequiredBadges
  );

  const customRatioNumbers = useMemo(
    () => customRatioParts.map((part) => Number(part)),
    [customRatioParts]
  );

  const isCustomRatioComplete = useMemo(
    () => customRatioParts.every((part) => part.trim() !== ''),
    [customRatioParts]
  );

  const isCustomRatioValid = isUsingCustomRatio
    ? isCustomRatioComplete &&
      customRatioNumbers.every(
        (value) => Number.isFinite(value) && value >= 0 && value <= 60
      )
    : true;

  const hasCustomRatioError =
    isUsingCustomRatio && isCustomRatioComplete && !isCustomRatioValid;

  const customRatioString = useMemo(() => {
    if (!isCustomRatioValid) return '';
    return customRatioNumbers
      .map((value) => String(Math.trunc(value)))
      .join('-');
  }, [customRatioNumbers, isCustomRatioValid]);

  useEffect(() => {
    if (!isUsingCustomRatio || !isCustomRatioValid) return;
    setSelectedRatio((prev) =>
      prev === customRatioString ? prev : customRatioString
    );
  }, [isUsingCustomRatio, isCustomRatioValid, customRatioString]);

  const ratioStringForCalculation = useMemo(() => {
    if (isUsingCustomRatio) {
      return isCustomRatioValid ? customRatioString : '';
    }
    return activeBreathingRatio?.ratio || selectedRatio;
  }, [
    activeBreathingRatio?.ratio,
    customRatioString,
    isCustomRatioValid,
    isUsingCustomRatio,
    selectedRatio,
  ]);

  const ratioValues = useMemo(() => {
    if (isUsingCustomRatio) {
      return isCustomRatioValid ? customRatioNumbers : [];
    }
    return parseRatioString(ratioStringForCalculation);
  }, [
    customRatioNumbers,
    isCustomRatioValid,
    isUsingCustomRatio,
    ratioStringForCalculation,
  ]);

  const hasRatioForSession = ratioValues.length > 0;

  // Compute total seconds per breathing cycle and how many cycles fit
  // into the selected duration (in minutes).
  const cycleSeconds = useMemo(
    () => ratioValues.reduce((acc, value) => acc + (Number(value) || 0), 0),
    [ratioValues]
  );

  const minimumDurationSeconds = cycleSeconds;
  const minimumDurationMinutes =
    minimumDurationSeconds > 0 ? minimumDurationSeconds / 60 : 0;
  const minimumDurationMinutesCeil =
    minimumDurationMinutes > 0
      ? Math.ceil(minimumDurationMinutes * 1000) / 1000
      : 0;

  const isDurationBelowMinimum =
    minimumDurationSeconds > 0 &&
    selectedDuration * 60 + 1e-6 < minimumDurationSeconds;

  const minimumDurationLabel = useMemo(
    () =>
      minimumDurationSeconds > 0
        ? formatSecondsLabel(minimumDurationSeconds)
        : null,
    [minimumDurationSeconds]
  );

  const totalCycles =
    cycleSeconds > 0 && selectedDuration > 0
      ? Math.ceil((selectedDuration * 60) / cycleSeconds)
      : 0;

  const isUsingCustomDuration =
    isCustomDuration || !isDurationOption(selectedDuration);

  useEffect(() => {
    if (minimumDurationSeconds <= 0) {
      minimumDurationRef.current = minimumDurationSeconds;
      return;
    }

    const previousMinimum = minimumDurationRef.current;
    const minimumIncreased = minimumDurationSeconds > previousMinimum + 1e-6;

    if (
      minimumIncreased &&
      selectedDuration * 60 + 1e-6 < minimumDurationSeconds
    ) {
      if (hasCustomTimeFeature) {
        const updatedDurationMinutes =
          minimumDurationMinutesCeil || minimumDurationMinutes;
        setSelectedDuration(updatedDurationMinutes);
        setIsCustomDuration(true);
        setCustomDuration((prev) => {
          const prevNumber = Number(prev);
          if (
            !prev ||
            Number.isNaN(prevNumber) ||
            prevNumber * 60 + 1e-6 < minimumDurationSeconds
          ) {
            return formatMinutesInputValue(updatedDurationMinutes);
          }
          return prev;
        });
      } else {
        const fallbackPreset = findPresetDurationForMinimum(
          minimumDurationSeconds
        );
        setSelectedDuration(fallbackPreset);
        setIsCustomDuration(false);
        setCustomDuration('');
      }
    }

    minimumDurationRef.current = minimumDurationSeconds;
  }, [
    minimumDurationSeconds,
    minimumDurationMinutesCeil,
    selectedDuration,
    minimumDurationMinutes,
    hasCustomTimeFeature,
  ]);

  useEffect(() => {
    if (hasCustomTimeFeature) return;
    if (!isCustomDuration && isDurationOption(selectedDuration)) return;

    setIsCustomDuration(false);
    if (!isDurationOption(selectedDuration)) {
      const fallbackDuration = findPresetDurationForMinimum(
        selectedDuration * 60
      );
      setSelectedDuration(fallbackDuration);
    }
    setCustomDuration('');
  }, [hasCustomTimeFeature, isCustomDuration, selectedDuration]);

  // (No modal) chooser will be shown inline under the control
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // Load background videos from DB
  useEffect(() => {
    (async () => {
      try {
        const list = await listBackgroundVideos();
        setBackgroundVideosFromDb(list.filter((v) => v));
        // If a background video id is already selected (e.g. from a template),
        // map it to the preview key so the UI shows the correct preview.
        if (list && selectedBackgroundVideoId) {
          const found = list.find(
            (b) => b && b.id === selectedBackgroundVideoId
          );
          if (found) setBackgroundVideo(keyFromFileName(found.file_name));
        }
      } catch (e) {
        // ignore silently — keep client-side defaults
      }
    })();
  }, [selectedBackgroundVideoId]);

  useEffect(() => {
    let active = true;

    if (!backgroundVideosFromDb || backgroundVideosFromDb.length === 0) {
      setMostPopularBackgroundVideoId(null);
      return () => {
        active = false;
      };
    }

    (async () => {
      const ids = backgroundVideosFromDb.map((video) => video.id);
      const topId = await getMostPopularBackgroundVideoId(ids);
      if (!active) return;
      setMostPopularBackgroundVideoId(topId);
    })();

    return () => {
      active = false;
    };
  }, [backgroundVideosFromDb]);

  // Keep backgroundVideo in sync when selectedBackgroundVideoId changes
  useEffect(() => {
    if (!selectedBackgroundVideoId) return;
    const found = backgroundVideosFromDb.find(
      (b) => b.id === selectedBackgroundVideoId
    );
    if (found) {
      setBackgroundVideo(keyFromFileName(found.file_name));
      if (selectedVisionBoardImage) {
        setSelectedVisionBoardImage(null);
      }
    }
  }, [
    selectedBackgroundVideoId,
    backgroundVideosFromDb,
    selectedVisionBoardImage,
  ]);

  useEffect(() => {
    if (hasTouchedBackgroundVideo) return;
    if (selectedBackgroundVideoId) return;
    if (!mostPopularBackgroundVideoId) return;
    const found = backgroundVideosFromDb.find(
      (video) => video.id === mostPopularBackgroundVideoId
    );
    if (!found) return;
    const key = keyFromFileName(found.file_name);
    if (!key || key === 'none') return;
    setSelectedBackgroundVideoId(found.id);
    setBackgroundVideo(key);
  }, [
    hasTouchedBackgroundVideo,
    selectedBackgroundVideoId,
    mostPopularBackgroundVideoId,
    backgroundVideosFromDb,
  ]);

  // Load the current user's vision board images (newest first)
  useEffect(() => {
    let active = true;

    if (!user?.id) {
      setVisionBoardImages([]);
      setSelectedVisionBoardImage(null);
      return () => {
        active = false;
      };
    }

    (async () => {
      // Fetch templates first
      let templates: VisionBoardTemplate[] = [];
      try {
        templates = await listVisionBoardTemplates(false);
      } catch (err) {
        console.error('Failed to fetch vision board templates', err);
      }

      const { data, error } = await supabase
        .from('vision_board_images')
        .select('id, image_url, preview_image_url, title, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (!active) return;

      if (error) {
        console.error('Failed to load vision board images', error);
        setVisionBoardImages([]);
        return;
      }

      const userRows = (data as VisionBoardImage[]) ?? [];
      const templateRows: DashboardVisionBoard[] = templates.map((t) => ({
        id: t.id,
        image_url: t.image_url ?? '',
        preview_image_url: t.preview_image_url ?? null,
        title: t.title,
        created_at: t.created_at,
        is_template: true,
      }));

      setVisionBoardImages([...templateRows, ...userRows]);
    })();

    return () => {
      active = false;
    };
  }, [user?.id]);

  // Broadcast helper for other tabs to react to deletions
  const broadcastVisionBoardDeleted = (id: string) => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('vision_board_images');
    channel.postMessage({ type: 'vision_board_deleted', id });
    channel.close();
  };

  const handleDeleteVisionBoard = async () => {
    if (!activeVisionBoardToDelete || isDeletingVisionBoard) return;
    setIsDeletingVisionBoard(true);
    setDeleteVisionBoardError(null);
    try {
      const filename = activeVisionBoardToDelete.filename || '';
      const baseName = filename.split('/').pop() || filename;
      const previewPath =
        ((activeVisionBoardToDelete as any)?.preview_filename as
          | string
          | undefined) ||
        `vision-board/previews/${baseName.replace(/\.jpg$/i, '-preview.jpg')}`;

      // Try to remove storage files
      const { error: storageError } = await supabase.storage
        .from('vision_board')
        .remove([filename, previewPath]);

      if (storageError) {
        setDeleteVisionBoardError(
          "We couldn't delete this vision board. Please try again."
        );
        return;
      }

      const { error: dbError } = await supabase
        .from('vision_board_images')
        .delete()
        .eq('id', activeVisionBoardToDelete.id);

      if (dbError) {
        setDeleteVisionBoardError(
          "We couldn't delete this vision board. Please try again."
        );
        return;
      }

      setVisionBoardImages((prev) =>
        prev.filter((item) => item.id !== activeVisionBoardToDelete.id)
      );
      if (selectedVisionBoardImage?.id === activeVisionBoardToDelete.id) {
        setSelectedVisionBoardImage(null);
      }
      broadcastVisionBoardDeleted(activeVisionBoardToDelete.id);
      setActiveVisionBoardToDelete(null);
    } catch (err) {
      console.error('[dashboard] failed to delete vision board', err);
      setDeleteVisionBoardError(
        "We couldn't delete this vision board. Please try again."
      );
    } finally {
      setIsDeletingVisionBoard(false);
    }
  };

  useEffect(() => {
    if (!selectedVisionBoardImage) return;
    if (
      visionBoardImages.some((row) => row.id === selectedVisionBoardImage.id)
    ) {
      return;
    }
    setSelectedVisionBoardImage(null);
  }, [selectedVisionBoardImage, visionBoardImages]);

  // Ref to the chooser grid so the options button can scroll to it
  const chooserRef = useRef<HTMLDivElement | null>(null);

  // Load active quotes from DB and pick a random one
  useEffect(() => {
    (async () => {
      try {
        const list = await listQuotes();
        if (list && list.length > 0) {
          const idx = Math.floor(Math.random() * list.length);
          setActiveQuote(list[idx] ?? null);
        }
      } catch (e) {
        console.error('Failed to load quotes', e);
      }
    })();
  }, []);

  // Build chooser entries and deduplicate keys (so 'none' isn't duplicated
  // if the DB contains an entry that maps to the same key). When available,
  // surface the most popular background video near the top (right after None).
  const videoEntries = useMemo(() => {
    const m = new Map<string, { key: string; label: string }>();
    backgroundVideosFromDb.forEach((b) => {
      const k = keyFromFileName(b.file_name);
      if (!m.has(k)) {
        m.set(k, { key: k, label: b.name });
      }
    });

    const entries = Array.from(m.values());

    // Sort entries by the minimum badge score required for the feature(s)
    const scoreForEntry = (entry: { key: string; label?: string }) => {
      const canonical = canonicalizeBackgroundVideoKey(entry.key);
      const rule = BACKGROUND_VIDEO_RULES[canonical];
      const normalizedFeatureKeys = Array.from(
        new Set(
          [...(rule?.featureKeys ?? []), rule?.featureKey].filter(
            (k): k is string => Boolean(k)
          )
        )
      );

      let badgeObjects: BadgeInfo[] = [];
      normalizedFeatureKeys.forEach((featureKey) => {
        const badgeList = featureBadgeRequirements[featureKey] ?? [];
        badgeObjects = badgeObjects.concat(badgeList || []);
      });

      if (rule?.requiredBadge && badgeObjects.length === 0) {
        // fallback: treat named requirement as score 0 unless a matching badge with score exists
        return 0;
      }

      const scores = badgeObjects
        .map((b) => (typeof b?.score === 'number' ? b.score : 0))
        .filter((s) => Number.isFinite(s));
      if (scores.length === 0) return 0;
      return Math.min(...scores);
    };

    entries.sort((a, b) => scoreForEntry(a) - scoreForEntry(b));

    // If we know the most popular video, surface it at the top.
    if (
      mostPopularBackgroundVideoKey &&
      mostPopularBackgroundVideoKey !== 'none'
    ) {
      const idx = entries.findIndex(
        (entry) => entry.key === mostPopularBackgroundVideoKey
      );
      if (idx > -1) {
        const removed = entries.splice(idx, 1);
        if (removed.length > 0) entries.unshift(removed[0]!);
      }
    }

    // Ensure the 'None' option appears at the end of the list for selection.
    const noneIdx = entries.findIndex((e) => e.key === 'none');
    if (noneIdx === -1) {
      entries.push({ key: 'none', label: 'None' });
    } else if (noneIdx > -1) {
      const removed = entries.splice(noneIdx, 1);
      if (removed.length > 0) entries.push(removed[0]!);
    }

    return entries;
  }, [
    backgroundVideosFromDb,
    mostPopularBackgroundVideoKey,
    featureBadgeRequirements,
  ]);

  const unlockedVideoEntries = useMemo(
    () => videoEntries.filter((entry) => canUseBackgroundVideo(entry.key)),
    [videoEntries, canUseBackgroundVideo]
  );

  const lockedVideoEntries = useMemo(
    () => videoEntries.filter((entry) => !canUseBackgroundVideo(entry.key)),
    [videoEntries, canUseBackgroundVideo]
  );

  const renderVideoPreview = (
    entryKey: string,
    displayName: string,
    isMostPopularVideo: boolean
  ) => {
    // Prefer DB preview image URL when available, otherwise fall back to
    // local static preview, and finally show an emoji if both images fail.
    const found = backgroundVideosFromDb.find(
      (v) => keyFromFileName(v.file_name) === entryKey
    );

    const previewFromDb = found?.preview_image_url
      ? buildBadgeAssetUrl(found.preview_image_url) || null
      : null;
    const localPreviewUrl = `/pictures/previews/${entryKey}.jpg`;

    return (
      <div className="relative h-28 w-full overflow-hidden rounded bg-gray-50">
        {entryKey !== 'none' ? (
          <>
            <img
              src={previewFromDb ?? localPreviewUrl}
              alt={displayName}
              className="h-full w-full object-cover"
              data-fallback="0"
              onError={(e) => {
                const img = e.target as HTMLImageElement;

                // If DB preview failed, try the local asset once.
                if (previewFromDb && img.dataset.fallback !== '1') {
                  img.dataset.fallback = '1';
                  img.src = localPreviewUrl;
                  return;
                }

                // Maintain legacy hyphen-split fallback for local assets.
                if (entryKey.includes('-')) {
                  const altKey = entryKey.split('-')[0];
                  const altSrc = `/pictures/previews/${altKey}.jpg`;
                  if (img.src && !img.src.endsWith(`${altKey}.jpg`)) {
                    img.src = altSrc;
                    return;
                  }
                }

                setFailedPreviews((p) => ({
                  ...p,
                  [entryKey]: true,
                }));
                img.style.display = 'none';
              }}
            />

            {failedPreviews[entryKey] && (
              <div className="flex h-full items-center justify-center text-3xl">
                {(videoPreviews[entryKey] || { emoji: '⬜' }).emoji}
              </div>
            )}

            {isMostPopularVideo && (
              <div className="item-selected absolute left-2 top-2 rounded-full px-2 py-0.5 text-xs font-semibold">
                Most Popular
              </div>
            )}
          </>
        ) : (
          <div className="h-full w-full bg-teal-300" />
        )}
      </div>
    );
  };

  const getBackgroundVideoMetadata = (entry: {
    key: string;
    label?: string;
  }) => {
    const canonicalKey = canonicalizeBackgroundVideoKey(entry.key);
    const rule = BACKGROUND_VIDEO_RULES[canonicalKey];
    const displayName =
      entry.label ||
      rule?.displayName ||
      videoPreviews[entry.key]?.label ||
      (canonicalKey !== entry.key
        ? videoPreviews[canonicalKey]?.label
        : undefined) ||
      entry.key;
    return { canonicalKey, rule, displayName };
  };

  const getBadgeRequirementsForEntry = (entry: {
    key: string;
    label?: string;
  }) => {
    const { rule } = getBackgroundVideoMetadata(entry);
    const normalizedFeatureKeys = Array.from(
      new Set(
        [...(rule?.featureKeys ?? []), rule?.featureKey].filter(
          (key): key is string => Boolean(key)
        )
      )
    );
    const badgeMap = new Map<string, BadgeInfo>();

    normalizedFeatureKeys.forEach((featureKey) => {
      const badgeList = featureBadgeRequirements[featureKey] ?? [];
      badgeList.forEach((badge, index) => {
        if (!badge) return;
        const identifier =
          badge.id ?? badge.name ?? badge.image_url ?? `${featureKey}-${index}`;
        if (!identifier || badgeMap.has(identifier)) return;
        badgeMap.set(identifier, badge);
      });
    });

    const dedupedBadges = Array.from(badgeMap.values());
    const fallbackNames = rule?.requiredBadge ? [rule.requiredBadge] : [];
    const resolvedNames = dedupedBadges
      .map((badge) => badge.name)
      .filter((name): name is string => Boolean(name));
    const lookupNames =
      resolvedNames.length > 0 ? resolvedNames : fallbackNames;
    const message = formatLockedModalMessage(lookupNames);
    const badgeObjects =
      dedupedBadges.length > 0
        ? dedupedBadges
        : fallbackNames.map((name) => ({ name }));

    return { badgeObjects, message };
  };

  const closeLockedVideoModal = () => setLockedVideoModalInfo(null);

  const handleLockedVideoClick = (entry: { key: string; label?: string }) => {
    const { displayName } = getBackgroundVideoMetadata(entry);
    const { badgeObjects, message } = getBadgeRequirementsForEntry(entry);
    setLockedVideoModalInfo({
      entryKey: entry.key,
      displayName,
      badgeObjects,
      message,
    });
  };

  const lockedVideoModalPrimaryBadge =
    lockedVideoModalInfo?.badgeObjects[0] ?? null;
  const lockedVideoModalBadgeList = lockedVideoModalInfo?.badgeObjects ?? [];

  const getPhaseLabel = (phase: BreathPhase): string => {
    switch (phase) {
      case 'inhale':
        return 'Breathe In';
      case 'hold-in':
        return 'Hold';
      case 'exhale':
        return 'Breathe Out';
      case 'hold-out':
        return 'Hold';
      default:
        return '';
    }
  };

  useEffect(() => {
    if (!isBreathing) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const [inhale = 4, holdIn = 0, exhale = 4, holdOut = 0] = ratioValues;

    const phases: { phase: BreathPhase; duration: number }[] = [];
    if (inhale > 0) phases.push({ phase: 'inhale', duration: inhale });
    if (holdIn > 0) phases.push({ phase: 'hold-in', duration: holdIn });
    if (exhale > 0) phases.push({ phase: 'exhale', duration: exhale });
    if (holdOut > 0) phases.push({ phase: 'hold-out', duration: holdOut });

    if (phases.length === 0) return;

    const firstPhase = phases[0];
    if (!firstPhase) return;

    let currentPhaseIndex = 0;
    let timeLeft = firstPhase.duration;

    setCurrentPhase(firstPhase.phase);
    setPhaseTimeLeft(timeLeft);

    timerRef.current = window.setInterval(() => {
      timeLeft -= 1;

      if (timeLeft <= 0) {
        currentPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        const currentPhase = phases[currentPhaseIndex];
        if (currentPhase) {
          timeLeft = currentPhase.duration;
          setCurrentPhase(currentPhase.phase);
        }
      }

      setPhaseTimeLeft(timeLeft);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isBreathing, ratioValues]);

  const isCustomRatioReady = !isUsingCustomRatio || isCustomRatioValid;

  const applyQuickPresetOverrides = useCallback(
    (preset?: QuickPreset) => {
      const overrides = preset?.overrides;
      if (!overrides) return;
      if (overrides.voiceGuidance !== undefined) {
        setVoiceGuidance(overrides.voiceGuidance);
      }
      if (overrides.phaseChime !== undefined) {
        setPhaseChime(overrides.phaseChime);
      }
      if (overrides.backgroundSound) {
        setBackgroundSound(overrides.backgroundSound);
      }
      if (overrides.affirmations !== undefined) {
        setAffirmations(overrides.affirmations);
        if (overrides.affirmations) {
          setAffirmationType(
            (overrides.affirmationType ??
              DEFAULT_AFFIRMATION_TYPE) as AffirmationType
          );
        }
      } else if (overrides.affirmationType) {
        setAffirmationType(overrides.affirmationType as AffirmationType);
      }
    },
    [
      setAffirmations,
      setAffirmationType,
      setVoiceGuidance,
      setPhaseChime,
      setBackgroundSound,
    ]
  );

  const scrollToTop = (after?: () => void) => {
    if (typeof window !== 'undefined' && window.scrollTo) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (after) {
          setTimeout(after, 150);
        }
      } catch {
        window.scrollTo(0, 0);
        after?.();
      }
    } else {
      after?.();
    }
  };

  // Ref for scrolling to the start of the "Customize Your Session" container
  const customizeSessionRef = useRef<HTMLDivElement | null>(null);

  const handleQuickPresetSelect = useCallback(
    (value: string) => {
      setSelectedQuickPresetId(value);

      if (!value) {
        logOptionSelect('preset', 'none', { action: 'clear_selection' });
        return;
      }

      const presetInfo = QUICK_PRESETS.find((preset) => preset.id === value);
      applyQuickPresetOverrides(presetInfo);
      logOptionSelect('preset', presetInfo?.label ?? value, {
        presetId: value,
        durationSeconds: presetInfo?.duration ?? null,
        ratio: presetInfo?.ratio ?? null,
      });
    },
    [logOptionSelect, applyQuickPresetOverrides]
  );

  const handleBreathingStyleChange = useCallback(
    (styleValue: BreathingStyle, label: string) => {
      setBreathingStyle(styleValue);
      logOptionSelect('breathing_style', styleValue, { label });
    },
    [logOptionSelect]
  );

  const handleBreathingRatioSelect = useCallback(
    (ratio: BreathingRatio) => {
      setHasUserSelectedBreathingRatio(true);
      setSelectedBreathingRatioId(ratio.id);
      setSelectedRatio(ratio.ratio);
      logOptionSelect('ratio', ratio.name ?? ratio.id, {
        ratioId: ratio.id,
        ratio: ratio.ratio,
      });
    },
    [logOptionSelect]
  );

  const handleCustomRatioSelection = useCallback(() => {
    setHasUserSelectedBreathingRatio(true);
    setSelectedBreathingRatioId('custom');
    setCustomRatioParts(() => {
      const parsed = parseRatioString(selectedRatio);
      if (
        parsed.length === 4 &&
        parsed.every((value) => value >= 0 && value <= 60)
      ) {
        return parsed.map((value) => String(value));
      }
      return [...DEFAULT_CUSTOM_RATIO];
    });
    logOptionSelect('ratio', 'custom_ratio');
  }, [logOptionSelect, selectedRatio]);

  const handleCustomRatioPartChange = useCallback(
    (index: number, nextValue: string, source: 'input' | 'blur' = 'input') => {
      setCustomRatioParts((prev) => {
        if (prev[index] === nextValue) {
          return prev;
        }
        const next = [...prev];
        next[index] = nextValue;
        const phaseLabel = CUSTOM_RATIO_LABELS[index];
        logOptionSelect('ratio', phaseLabel, {
          field: phaseLabel,
          value: nextValue,
          source,
          values: next,
        });
        return next;
      });
    },
    [logOptionSelect]
  );

  const handleDurationSelect = useCallback(
    (minutes: number) => {
      setSelectedDuration(minutes);
      setIsCustomDuration(false);
      logOptionSelect('duration', `${minutes}`, {
        type: 'preset',
        minutes,
      });
    },
    [logOptionSelect]
  );

  const handleCustomDurationButton = useCallback(() => {
    setIsCustomDuration(true);
    logOptionSelect('duration', 'custom', { action: 'open_custom_time' });
    if (customDuration) {
      const parsed = Number(customDuration);
      if (!Number.isNaN(parsed)) {
        setSelectedDuration(parsed);
      }
    }
  }, [customDuration, logOptionSelect]);

  const handleCustomDurationChange = useCallback(
    (value: string) => {
      setCustomDuration(value);
      const num = Number(value);
      if (!Number.isNaN(num)) {
        setSelectedDuration(num);
      }
      const label = value?.trim() ? value : 'custom_duration';
      logOptionSelect('duration', label, {
        value,
        source: 'custom_input',
      });
    },
    [logOptionSelect]
  );

  const handleAffirmationsToggle = useCallback(
    (enabled: boolean) => {
      setAffirmations(enabled);
      if (!enabled) {
        setAffirmationType('');
        setAffirmationWhisperMode(false);
      }
      logToggleEvent('affirmation', enabled ? 'on' : 'off', {
        target: 'affirmations',
      });
    },
    [logToggleEvent]
  );

  const handleAffirmationWhisperModeToggle = useCallback(
    (enabled: boolean) => {
      setAffirmationWhisperMode(enabled);
      setAffirmationMode(enabled ? 'subliminal' : 'regular');
      // When Subliminal is selected, enable Voice Guidance; though we don't force disable it for Regular anymore
      if (enabled) {
        setVoiceGuidance(true);
      }
      logToggleEvent('affirmation', enabled ? 'on' : 'off', {
        target: 'affirmation_whisper_mode',
      });
    },
    [logToggleEvent]
  );

  const handleAffirmationTypeChange = useCallback(
    (value: AffirmationType) => {
      setAffirmationType(value);
      logOptionSelect('affirmation', value, { type: value });
    },
    [logOptionSelect]
  );

  const handleVoiceGuidanceToggle = useCallback(
    (enabled: boolean) => {
      setVoiceGuidance(enabled);
      logToggleEvent('audio', enabled ? 'on' : 'off', {
        target: 'voice_guidance',
      });
    },
    [logToggleEvent]
  );

  const handlePhaseChimeToggle = useCallback(
    (enabled: boolean) => {
      setPhaseChime(enabled);
      logToggleEvent('audio', enabled ? 'on' : 'off', {
        target: 'phase_chime',
      });
    },
    [logToggleEvent]
  );

  const handleIntroVoiceToggle = useCallback(
    (enabled: boolean) => {
      setIntroVoice(enabled);
      logToggleEvent('audio', enabled ? 'on' : 'off', {
        target: 'intro_voice',
      });
    },
    [logToggleEvent]
  );

  const handlePlayInBackgroundToggle = useCallback(
    (enabled: boolean) => {
      setPlayInBackground(enabled);
      logToggleEvent('other', enabled ? 'on' : 'off', {
        target: 'play_in_background',
      });
    },
    [logToggleEvent]
  );

  const handleHideCycleCountToggle = useCallback(
    (shouldHide: boolean) => {
      setHideCycleCount(shouldHide);
      logToggleEvent('other', shouldHide ? 'off' : 'on', {
        target: 'show_cycle_count',
      });
    },
    [logToggleEvent]
  );

  const handleHideTimeLeftToggle = useCallback(
    (shouldHide: boolean) => {
      setHideTimeLeft(shouldHide);
      logToggleEvent('other', shouldHide ? 'off' : 'on', {
        target: 'show_time_left',
      });
    },
    [logToggleEvent]
  );

  const handleBackgroundSoundToggle = useCallback(
    (value: 'on' | 'off') => {
      setBackgroundSound(value);
      logToggleEvent('audio', value, { target: 'background_sound' });
    },
    [logToggleEvent]
  );

  const handleMusicToggle = useCallback(
    (enabled: boolean) => {
      setMusicEnabled(enabled);
      if (!enabled) {
        setSelectedMusicTrackId(null);
        if (musicPreviewAudioRef.current) {
          musicPreviewAudioRef.current.pause();
          setPlayingMusicPreview(null);
        }
      }
      logToggleEvent('audio', enabled ? 'on' : 'off', { target: 'music' });
    },
    [logToggleEvent]
  );

  const handleMusicTrackSelect = useCallback(
    (trackId: string) => {
      setSelectedMusicTrackId(trackId);
      const track = musicTracks.find((t) => t.id === trackId);
      logOptionSelect('music', track?.name || trackId, { id: trackId });
    },
    [logOptionSelect, musicTracks]
  );

  const toggleMusicPreview = (url: string) => {
    if (playingMusicPreview === url) {
      musicPreviewAudioRef.current?.pause();
      setPlayingMusicPreview(null);
    } else {
      if (musicPreviewAudioRef.current) {
        // Preview should be quieter than session music — keep it subtle.
        try {
          musicPreviewAudioRef.current.volume = 0.25;
        } catch (e) {
          /* ignore */
        }
        musicPreviewAudioRef.current.src = url;
        musicPreviewAudioRef.current.play().catch(() => {
          console.error('Failed to play music preview');
          setPlayingMusicPreview(null);
        });
        setPlayingMusicPreview(url);
      }
    }
  };

  const applyColorBackgroundSelection = useCallback(
    (value: string, source: 'picker' | 'preset') => {
      setHasTouchedBackgroundVideo(true);
      setColorBackgroundMode('custom');
      setIsColorBackgroundSelected(true);
      setIsGradientBackgroundSelected(false);
      setBackgroundColor(value);
      setBackgroundVideo('none');
      setSelectedBackgroundVideoId(null);
      setSelectedVisionBoardImage(null);
      if (wishToEnableBackgroundSound) {
        setBackgroundSound('on');
      }
      setWishToEnableBackgroundSound(false);
      logOptionSelect('color_background', value, { source });
    },
    [
      logOptionSelect,
      wishToEnableBackgroundSound,
      setBackgroundSound,
      setWishToEnableBackgroundSound,
    ]
  );

  const applyGradientBackgroundSelection = useCallback(
    (value: string, source: 'preset') => {
      setHasTouchedBackgroundVideo(true);
      setColorBackgroundMode('gradient');
      setIsGradientBackgroundSelected(true);
      setIsColorBackgroundSelected(false);
      setBackgroundGradient(value);
      setBackgroundVideo('none');
      setSelectedBackgroundVideoId(null);
      setSelectedVisionBoardImage(null);
      if (wishToEnableBackgroundSound) {
        setBackgroundSound('on');
      }
      setWishToEnableBackgroundSound(false);
      logOptionSelect('gradient_background', value, { source });
    },
    [
      logOptionSelect,
      wishToEnableBackgroundSound,
      setBackgroundSound,
      setWishToEnableBackgroundSound,
    ]
  );

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('vision_board_images');
    const handleMessage = (event: MessageEvent) => {
      if (!event?.data || event.data.type !== 'vision_board_deleted') return;
      const deletedId = String(event.data.id || '');
      if (!deletedId) return;
      setVisionBoardImages((prev) =>
        prev.filter((item) => item.id !== deletedId)
      );
      if (selectedVisionBoardImage?.id === deletedId) {
        applyColorBackgroundSelection(DEFAULT_COLOR_BACKGROUND, 'preset');
      }
    };
    channel.addEventListener('message', handleMessage);
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, [applyColorBackgroundSelection, selectedVisionBoardImage]);

  const openCustomRatioModal = () => setShowCustomRatioUnlockModal(true);
  const closeCustomRatioModal = () => setShowCustomRatioUnlockModal(false);
  const openCustomTimeModal = () => setShowCustomTimeLockedModal(true);
  const closeCustomTimeModal = () => setShowCustomTimeLockedModal(false);
  const handleViewBadges = () => {
    closeCustomRatioModal();
    closeCustomTimeModal();
    closeLockedVideoModal();
    navigate('/badges');
  };

  const handleStartBreathing = () => {
    const backgroundType = isColorBackgroundSelected
      ? 'color'
      : isGradientBackgroundSelected
        ? 'gradient'
        : selectedVisionBoardImage
          ? 'image'
          : 'video';
    const backgroundSource =
      (isColorBackgroundSelected ? backgroundColor : null) ??
      (isGradientBackgroundSelected ? backgroundGradient : null) ??
      selectedVisionBoardImage?.image_url ??
      backgroundVideo;
    const backgroundVideoForSession =
      isColorBackgroundSelected ||
      isGradientBackgroundSelected ||
      selectedVisionBoardImage
        ? 'none'
        : backgroundVideo;

    const selectedAffirmationRecord = affirmationTypesFromDb.find(
      (t) => t.id === affirmationType
    );
    // If not found in DB (e.g. still loading), try to derive the filename as a fallback
    const affirmationAudioUrl =
      selectedAffirmationRecord?.audio_url ||
      (affirmationType
        ? `affirmation_${affirmationType.toLowerCase().replace(/[^a-z0-9]+/g, '_')}.mp3`
        : undefined);

    const baseEventValue: DashboardEventValue = {
      ratioId: selectedBreathingRatioId || null,
      ratio: ratioStringForCalculation,
      customRatio:
        isUsingCustomRatio && isCustomRatioValid
          ? customRatioNumbers.map((value) => Math.trunc(value))
          : null,
      durationMinutes: selectedDuration,
      isCustomDuration: isUsingCustomDuration,
      breathingStyle,
      backgroundVideo: backgroundVideoForSession,
      backgroundType,
      backgroundSource,
      backgroundSound,
      voiceGuidance,
      phaseChime,
      introVoice,
      playInBackground,
      hideCycleCount,
      hideTimeLeft,
      musicEnabled,
      musicTrackId: selectedMusicTrackId,
      affirmationsEnabled: affirmations,
      affirmationType: affirmations ? affirmationType : null,
      affirmationAudioUrl: affirmations ? affirmationAudioUrl : null,
      affirmationMode: affirmations ? affirmationMode : null,
      affirmationWhisperMode: affirmations ? affirmationWhisperMode : false,
    };

    const logStartAttempt = (
      status: 'started' | 'blocked',
      reason?: string
    ) => {
      logDashboardInteraction({
        eventType: 'start_click',
        eventCategory: 'custom',
        eventLabel: 'custom_session',
        eventValue: {
          ...baseEventValue,
          status,
          reason: reason ?? null,
        },
      });
    };

    if (!hasCustomRatioFeature && selectedBreathingRatioId === 'custom') {
      logStartAttempt('blocked', 'custom_ratio_locked');
      openCustomRatioModal();
      return;
    }

    if (!hasCustomTimeFeature && isUsingCustomDuration) {
      logStartAttempt('blocked', 'custom_time_locked');
      openCustomTimeModal();
      return;
    }

    if (isDurationBelowMinimum || !isCustomRatioReady || !hasRatioForSession) {
      logStartAttempt('blocked', 'validation_failed');
      return;
    }

    logStartAttempt('started');

    // Scroll to top before navigating so the breathing page starts at top.
    // Delay navigation briefly to allow the browser to repaint and perform
    // the scroll; without the delay a route change may cancel the scroll.
    const doNavigate = () => {
      // Prevent preview audio from overlapping the session audio.
      if (musicPreviewAudioRef.current) {
        try {
          musicPreviewAudioRef.current.pause();
          musicPreviewAudioRef.current.currentTime = 0;
          musicPreviewAudioRef.current.src = '';
          musicPreviewAudioRef.current.load();
        } catch {
          // ignore
        }
      }
      setPlayingMusicPreview(null);

      navigate('/breathing', {
        state: {
          backgroundVideo: backgroundVideoForSession,
          backgroundType,
          backgroundSource,
          backgroundImageUrl: selectedVisionBoardImage?.image_url ?? null,
          backgroundColor: isColorBackgroundSelected ? backgroundColor : null,
          backgroundSound,
          voiceGuidance,
          affirmations,
          affirmationType,
          affirmationAudioUrl,
          affirmationMode,
          affirmationWhisperMode: affirmations ? affirmationWhisperMode : false,
          phaseChime,
          introVoice,
          playInBackground: playInBackground,
          hideCycleCount: hideCycleCount,
          hideTimeLeft: hideTimeLeft,
          musicEnabled,
          musicTrackId: selectedMusicTrackId,
          duration: selectedDuration,
          isCustomDuration: isUsingCustomDuration,
          ratio: ratioStringForCalculation,
          customRatioParts:
            isUsingCustomRatio && isCustomRatioValid
              ? customRatioNumbers.map((value) => Math.trunc(value))
              : undefined,
          breathingStyle,
        },
      });
    };

    scrollToTop(doNavigate);
  };

  const startQuickPreset = () => {
    if (!selectedQuickPreset) return;
    const p = selectedQuickPreset;
    const presetBreathingStyle: BreathingStyle = 'line';
    const overrides = p.overrides;
    const finalVoiceGuidance = overrides?.voiceGuidance ?? voiceGuidance;
    const finalPhaseChime = overrides?.phaseChime ?? phaseChime;
    const finalAffirmations = overrides?.affirmations ?? affirmations;
    const finalAffirmationType = (overrides?.affirmationType ??
      affirmationType) as AffirmationType;
    const selectedAffirmationRecordForPreset = affirmationTypesFromDb.find(
      (t) => t.id === finalAffirmationType
    );
    const affirmationAudioUrl = selectedAffirmationRecordForPreset?.audio_url;

    const finalAffirmationMode = overrides?.affirmationMode ?? affirmationMode;
    const finalBackgroundSound =
      overrides?.backgroundSound ?? (p.audio === 'on' ? 'on' : 'off');
    logDashboardInteraction({
      eventType: 'start_click',
      eventCategory: 'preset',
      eventLabel: p.id,
      eventValue: {
        presetId: p.id,
        presetLabel: p.label,
        durationSeconds: p.duration,
        background: p.background,
        audio: p.audio,
        breathingStyle: presetBreathingStyle,
      },
    });
    // Build ratio string, trimming trailing zeros for compatibility
    const parts = [
      p.ratio.inhale,
      p.ratio.hold_full,
      p.ratio.exhale,
      p.ratio.hold_empty,
    ];
    const trimmed = [...parts];
    while (trimmed.length > 1 && trimmed[trimmed.length - 1] === 0)
      trimmed.pop();
    const ratioStr = trimmed.map((v) => String(Math.trunc(v || 0))).join('-');

    const doNavigate = () => {
      navigate('/breathing', {
        state: {
          backgroundVideo: canonicalizeBackgroundVideoKey(p.background),
          backgroundSound: finalBackgroundSound,
          voiceGuidance: finalVoiceGuidance,
          affirmations: finalAffirmations,
          affirmationType: finalAffirmations ? finalAffirmationType : null,
          affirmationAudioUrl: finalAffirmations
            ? affirmationAudioUrl
            : undefined,
          affirmationMode: finalAffirmations ? finalAffirmationMode : null,
          affirmationWhisperMode: finalAffirmations
            ? affirmationWhisperMode
            : false,
          phaseChime: finalPhaseChime,
          // Prefer the preset's explicit introVoice setting when present.
          // This ensures quick presets like Panic Attack Relief that opt out
          // of the intro voice will not play it regardless of the user's
          // global preference.
          introVoice: p.introVoice ?? introVoice,
          playInBackground: playInBackground,
          hideCycleCount: hideCycleCount,
          hideTimeLeft: hideTimeLeft,
          duration: Math.trunc(p.duration / 60),
          isCustomDuration: false,
          ratio: ratioStr,
          customRatioParts: parts,
          breathingStyle: presetBreathingStyle,
        },
      });
    };

    scrollToTop(doNavigate);
  };

  const startEditingNotes = (session: {
    id: string;
    notes?: string | null;
  }) => {
    setEditingSessionId(session.id);
    setEditingNotes(session.notes || '');
    setNotesError(null);
  };

  const cancelEditingNotes = () => {
    setEditingSessionId(null);
    setEditingNotes('');
    setNotesError(null);
  };

  const saveEditingNotes = async () => {
    if (!editingSessionId) return;
    setNotesSaving(true);
    setNotesError(null);
    try {
      const normalizedNotes =
        editingNotes.trim().length === 0 ? null : editingNotes;
      const updated = await updateSessionNotes(
        editingSessionId,
        normalizedNotes
      );
      setRecentSessionsFromDb((prev) =>
        prev.map((s) =>
          s.id === updated.id ? { ...s, notes: updated.notes } : s
        )
      );
      cancelEditingNotes();
    } catch (err) {
      console.error(err);
      setNotesError('Unable to save notes. Please try again.');
    } finally {
      setNotesSaving(false);
    }
  };

  // Load templates for the current user on mount
  useEffect(() => {
    (async () => {
      try {
        const list = await listTemplatesForCurrentUser();
        setTemplates(list);
      } catch (e) {
        // Silently ignore in UI; user may be unauthenticated
      }
    })();
  }, []);

  const getTemplateNumber = (
    tpl: SessionTemplate,
    key: string
  ): number | undefined => {
    const v = (tpl as unknown as Record<string, unknown>)[key];
    if (v === null || v === undefined) return undefined;
    const n = Number(v);
    if (!Number.isFinite(n)) return undefined;
    return Math.trunc(n);
  };

  const applyTemplate = (tpl: SessionTemplate) => {
    setHasTouchedBackgroundVideo(true);
    setVoiceGuidance(Boolean(tpl.voice_guidance));
    setPhaseChime(Boolean(tpl.phase_chime));
    setBackgroundSound(tpl.background_sound ? 'on' : 'off');
    // Prefer new `background_play` column when present, fall back to legacy `play_in_background`
    setPlayInBackground(
      Boolean((tpl as any).background_play ?? tpl.play_in_background)
    );
    setHideCycleCount(Boolean(tpl.hide_cycle_count));
    setHideTimeLeft(Boolean(tpl.hide_time_left));
    setIntroVoice(Boolean(tpl.intro_voice));
    setMusicEnabled(Boolean(tpl.music_enabled));
    if (tpl.music_enabled && tpl.music_track_id) {
      // Check if track is still active/available
      if (musicTracks.some((t) => t.id === tpl.music_track_id)) {
        setSelectedMusicTrackId(tpl.music_track_id);
      } else {
        setSelectedMusicTrackId(null);
        setMusicEnabled(false);
      }
    } else {
      setSelectedMusicTrackId(null);
    }
    // Restore affirmations (new fields added to session_templates)
    setAffirmations(Boolean(tpl.affirmation));
    if (tpl.affirmation) {
      setAffirmationType(
        (tpl.affirmation_type ?? DEFAULT_AFFIRMATION_TYPE) as AffirmationType
      );
      setAffirmationMode(tpl.affirmation_mode || 'regular');
      setAffirmationWhisperMode(Boolean(tpl.affirmation_whisper_mode));
    } else {
      setAffirmationType('');
      setAffirmationMode('regular');
      setAffirmationWhisperMode(false);
    }
    // Resolve breathing style by id (preferred) or fall back to legacy text.
    if (tpl.breathing_style_id) {
      const foundStyle = breathingStylesFromDb.find(
        (s) => s.id === tpl.breathing_style_id
      );
      const name = foundStyle?.name?.trim().toLowerCase();
      if (name && isBreathingStyle(name)) {
        setBreathingStyle(name as typeof DEFAULT_BREATHING_STYLE);
      } else {
        setBreathingStyle(DEFAULT_BREATHING_STYLE);
      }
    } else {
      setBreathingStyle(
        tpl.breathing_style && isBreathingStyle(tpl.breathing_style)
          ? tpl.breathing_style
          : DEFAULT_BREATHING_STYLE
      );
    }

    // Restore vision board if present
    if (tpl.vision_board_id) {
      const foundImage = visionBoardImages.find(
        (img) => img.id === tpl.vision_board_id
      );
      if (foundImage) {
        setSelectedVisionBoardImage(foundImage);
        // If a vision board is selected, it usually overrides background video in the UI
        setSelectedBackgroundVideoId(null);
        setBackgroundVideo('none');
      } else {
        setSelectedVisionBoardImage(null);
      }
    } else {
      setSelectedVisionBoardImage(null);
    }

    // Apply duration if it exists
    if (tpl.duration !== null && tpl.duration !== undefined) {
      const duration = tpl.duration;
      // Check if it's a preset duration (1, 3, 5, 10)
      if (isDurationOption(duration)) {
        setSelectedDuration(duration);
        setIsCustomDuration(false);
        setCustomDuration('');
      } else if (hasCustomTimeFeature) {
        // It's a custom duration
        setSelectedDuration(duration);
        setIsCustomDuration(true);
        setCustomDuration(String(duration));
      } else {
        openCustomTimeModal();
        const fallbackDuration = findPresetDurationForMinimum(duration * 60);
        setSelectedDuration(fallbackDuration);
        setIsCustomDuration(false);
        setCustomDuration('');
      }
    }

    // Apply breathing ratio id if present
    if (tpl.breathing_ratio_id) {
      setHasUserSelectedBreathingRatio(true);
      setSelectedBreathingRatioId(tpl.breathing_ratio_id ?? '');
      const found = breathingRatiosFromDb.find(
        (r) => r.id === tpl.breathing_ratio_id
      );
      if (found) setSelectedRatio(found.ratio);
    } else {
      if (!hasCustomRatioFeature) {
        openCustomRatioModal();
      } else {
        setHasUserSelectedBreathingRatio(true);
        // No breathing_ratio_id: template may contain explicit "other" parts.
        setSelectedBreathingRatioId('custom');

        const inh = getTemplateNumber(tpl, 'breathing_ratio_other_inhale');
        const holdFull = getTemplateNumber(
          tpl,
          'breathing_ratio_other_hold_full'
        );
        const exh = getTemplateNumber(tpl, 'breathing_ratio_other_exhale');
        const holdEmpty = getTemplateNumber(
          tpl,
          'breathing_ratio_other_hold_empty'
        );

        if (
          inh !== undefined ||
          holdFull !== undefined ||
          exh !== undefined ||
          holdEmpty !== undefined
        ) {
          const parts = [inh, holdFull, exh, holdEmpty].map((v) => {
            if (v === null || v === undefined)
              return String(DEFAULT_CUSTOM_RATIO[0]);
            return String(Math.trunc(Number(v) || 0));
          });
          setCustomRatioParts(parts);
          // Also set selectedRatio to reflect the custom values immediately.
          setSelectedRatio(parts.join('-'));
        }
      }
    }

    // Apply background video id if present
    if (tpl.background_videos_id) {
      setSelectedBackgroundVideoId(tpl.background_videos_id);
      setIsColorBackgroundSelected(false);
      setIsGradientBackgroundSelected(false);
      setSelectedVisionBoardImage(null);
      const found = backgroundVideosFromDb.find(
        (b) => b.id === tpl.background_videos_id
      );
      if (found) {
        setBackgroundVideo(keyFromFileName(found.file_name));
      }
    } else {
      setSelectedBackgroundVideoId(null);
    }
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const tpl = templates.find((t) => t.id === templateId);
    if (tpl) applyTemplate(tpl);
  };

  const startTemplateSession = (templateId: string) => {
    if (!templateId) return;
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;

    // Apply template to update the UI as well
    applyTemplate(tpl);

    const templateBreathingStyle = (() => {
      if (tpl.breathing_style_id) {
        const found = breathingStylesFromDb.find(
          (s) => s.id === tpl.breathing_style_id
        );
        const name = found?.name?.trim().toLowerCase();
        if (name && isBreathingStyle(name)) return name as BreathingStyle;
      }
      return tpl.breathing_style && isBreathingStyle(tpl.breathing_style)
        ? tpl.breathing_style
        : DEFAULT_BREATHING_STYLE;
    })();

    // Determine ratio and custom parts to pass to the breathing page
    let ratioStr = '';
    let customParts: number[] | undefined;

    if (tpl.breathing_ratio_id) {
      const found = breathingRatiosFromDb.find(
        (r) => r.id === tpl.breathing_ratio_id
      );
      ratioStr = found?.ratio ?? selectedRatio;
    } else {
      if (!hasCustomRatioFeature) {
        openCustomRatioModal();
        return;
      }
      const inh = getTemplateNumber(tpl, 'breathing_ratio_other_inhale') ?? 0;
      const holdFull =
        getTemplateNumber(tpl, 'breathing_ratio_other_hold_full') ?? 0;
      const exh = getTemplateNumber(tpl, 'breathing_ratio_other_exhale') ?? 0;
      const holdEmpty =
        getTemplateNumber(tpl, 'breathing_ratio_other_hold_empty') ?? 0;
      const parts = [inh, holdFull, exh, holdEmpty].map((v) =>
        Math.trunc(Number(v) || 0)
      );
      customParts = parts;
      ratioStr = parts.map((v) => String(v)).join('-');
    }

    // Background video key
    let bgKey = 'none';
    if (tpl.background_videos_id) {
      const found = backgroundVideosFromDb.find(
        (b) => b.id === tpl.background_videos_id
      );
      if (found) bgKey = keyFromFileName(found.file_name);
    }

    // Vision board image URL
    let visionBoardUrl: string | null = null;
    if (tpl.vision_board_id) {
      const found = visionBoardImages.find(
        (img) => img.id === tpl.vision_board_id
      );
      if (found) visionBoardUrl = found.image_url;
    }

    const bgSound = tpl.background_sound ? 'on' : 'off';
    const musicTrackId =
      tpl.music_enabled && tpl.music_track_id ? tpl.music_track_id : null;

    // Navigate to breathing with the template values
    navigate('/breathing', {
      state: {
        backgroundVideo: bgKey,
        backgroundImageUrl: visionBoardUrl,
        backgroundSound: bgSound,
        voiceGuidance: Boolean(tpl.voice_guidance),
        phaseChime: Boolean(tpl.phase_chime),
        introVoice: Boolean(tpl.intro_voice),
        playInBackground: Boolean(
          tpl.play_in_background || (tpl as any).background_play
        ),
        hideCycleCount: Boolean(tpl.hide_cycle_count),
        hideTimeLeft: Boolean(tpl.hide_time_left),
        musicEnabled: Boolean(tpl.music_enabled),
        musicTrackId,
        duration: tpl.duration ?? selectedDuration,
        ratio: ratioStr,
        customRatioParts: customParts,
        breathingStyle: templateBreathingStyle,
        affirmations: Boolean(tpl.affirmation),
        affirmationType: tpl.affirmation
          ? (tpl.affirmation_type ?? null)
          : null,
      },
    });
  };

  const handleSaveTemplate = async () => {
    // If user is not signed in, prompt them to register/sign in
    if (!user) {
      setShowRegisterModal(true);
      return;
    }
    if (!templateName.trim()) return;
    if (isDurationBelowMinimum || !isCustomRatioReady) return;
    setIsSavingTemplate(true);
    try {
      const selectedBreathingStyleId =
        breathingStylesFromDb.find(
          (s) => s.name?.trim().toLowerCase() === breathingStyle
        )?.id ?? null;

      const saved = await createTemplate({
        template_name: templateName.trim(),
        voice_guidance: voiceGuidance,
        phase_chime: phaseChime,
        background_sound: backgroundSound === 'on',
        play_in_background: playInBackground,
        // Persist new column too for newer DB schemas
        background_play: playInBackground,
        hide_cycle_count: hideCycleCount,
        hide_time_left: hideTimeLeft,
        intro_voice: introVoice,
        music_enabled: musicEnabled,
        music_track_id: musicEnabled ? selectedMusicTrackId : null,
        duration: selectedDuration,
        breathing_ratio_id:
          selectedBreathingRatioId && selectedBreathingRatioId !== 'custom'
            ? selectedBreathingRatioId
            : null,
        // If the user is using a custom "Other" ratio, persist the explicit parts
        breathing_ratio_other_inhale:
          isUsingCustomRatio && isCustomRatioValid
            ? Math.trunc(customRatioNumbers[0] ?? 0)
            : null,
        breathing_ratio_other_exhale:
          isUsingCustomRatio && isCustomRatioValid
            ? Math.trunc(customRatioNumbers[2] ?? 0)
            : null,
        breathing_ratio_other_hold_full:
          isUsingCustomRatio && isCustomRatioValid
            ? Math.trunc(customRatioNumbers[1] ?? 0)
            : null,
        breathing_ratio_other_hold_empty:
          isUsingCustomRatio && isCustomRatioValid
            ? Math.trunc(customRatioNumbers[3] ?? 0)
            : null,
        background_videos_id: selectedBackgroundVideoId,
        vision_board_id: selectedVisionBoardImage?.id ?? null,
        breathing_style: breathingStyle,
        breathing_style_id: selectedBreathingStyleId,
        affirmation: affirmations,
        affirmation_type: affirmations ? affirmationType || null : null,
        affirmation_mode: affirmations ? affirmationMode : null,
        affirmation_whisper_mode: affirmations ? affirmationWhisperMode : false,
      });
      setTemplates((prev) => {
        const next = [...prev, saved].sort((a, b) =>
          a.template_name.localeCompare(b.template_name)
        );
        return next;
      });
      setTemplateName('');
      setSelectedTemplateId(saved.id);
      if (
        customizeSessionRef?.current &&
        typeof customizeSessionRef.current.scrollIntoView === 'function'
      ) {
        try {
          customizeSessionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        } catch {
          customizeSessionRef.current.scrollIntoView();
        }
      }
    } catch (e) {
      // Could show a toast/notification in the future
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleStopBreathing = () => {
    setIsBreathing(false);
    setIsFullMode(false);
    setCurrentPhase('inhale');
    setPhaseTimeLeft(4);
  };

  // Map DB sessions into a simple shape used by the UI (minutes, date, ratio)
  const displaySessions = useMemo(() => {
    if (!recentSessionsFromDb || recentSessionsFromDb.length === 0) return [];

    return recentSessionsFromDb.map((s) => {
      const ratioFromDb = s.breathing_ratio_id
        ? breathingRatiosFromDb.find((r) => r.id === s.breathing_ratio_id)
        : null;

      const breathingRatioName = ratioFromDb?.name || null;

      // Notes may contain a Ratio line; keep whole notes for display
      const notesText = s.notes || '';

      const createdAt =
        s.created_at || s.session_date || new Date().toISOString();
      const minutesSeconds = formatMinutesSeconds(s.total_duration || 0);
      const cycles = Number(s.cycles_completed || 0);
      const visibility = s.leaderboard_eligibility ? 'Public' : 'Private';

      return {
        id: s.id,
        breathing_ratio_name: breathingRatioName || 'Custom',
        minutesSeconds,
        notes: notesText,
        created_at: createdAt,
        cycles,
        visibility,
        user_timezone: s.user_timezone ?? null,
      };
    });
  }, [recentSessionsFromDb, breathingRatiosFromDb]);

  const totalSecondsForUser = useMemo(() => {
    const source =
      allSessionsFromDb && allSessionsFromDb.length > 0
        ? allSessionsFromDb
        : recentSessionsFromDb;

    if (!source || source.length === 0) return 0;

    return source.reduce(
      (acc, session) => acc + (Number(session.total_duration) || 0),
      0
    );
  }, [allSessionsFromDb, recentSessionsFromDb]);

  const dayStreak = useMemo(() => {
    const source =
      allSessionsFromDb && allSessionsFromDb.length > 0
        ? allSessionsFromDb
        : recentSessionsFromDb;

    if (!source || source.length === 0) return 0;

    const dateStrings = new Set<string>();
    source.forEach((session) => {
      const dt = session.created_at || session.session_date;
      try {
        const d = new Date(dt || '');
        if (isNaN(d.getTime())) return;
        // Use YYYY-MM-DD (UTC) for stable day comparisons
        dateStrings.add(d.toISOString().slice(0, 10));
      } catch (e) {
        // ignore parse errors
      }
    });

    if (dateStrings.size === 0) return 0;

    // Reuse centralized helper for consistent streak calculation
    // Use centralized helper for streak calculation
    return calculateCurrentStreak(dateStrings);
  }, [allSessionsFromDb, recentSessionsFromDb]);

  // Info tooltip component used in audio options (hover on desktop, tap on mobile)
  const InfoTooltip: React.FC<{ text: string; ariaLabel?: string }> = ({
    text,
    ariaLabel,
  }) => {
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);
    const [posLeft, setPosLeft] = useState<number>(0);
    const [posTop, setPosTop] = useState<number>(0);
    const [placeBelow, setPlaceBelow] = useState(false);
    const srId = useRef(
      'affirmation-tooltip-' + Math.random().toString(36).slice(2, 9)
    ).current;

    const show = useCallback(() => {
      const btn = btnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const vw = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      );
      const tooltipWidth = Math.min(240, vw - 24);
      let left = rect.left + rect.width / 2;
      left = Math.max(
        12 + tooltipWidth / 2,
        Math.min(left, vw - 12 - tooltipWidth / 2)
      );
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const below = spaceBelow > 40 && spaceBelow > spaceAbove;
      const topBase = below ? rect.bottom : rect.top;
      setPlaceBelow(below);
      setPosLeft(left);
      setPosTop(topBase);
      setVisible(true);
    }, []);

    const hide = useCallback(() => setVisible(false), []);

    useEffect(() => {
      if (!visible) return;
      const onDocDown = (ev: Event) => {
        const t = ev.target as Node | null;
        if (!t) return;
        if (btnRef.current && btnRef.current.contains(t)) return;
        if (tooltipRef.current && tooltipRef.current.contains(t)) return;
        hide();
      };
      document.addEventListener('pointerdown', onDocDown, true);
      return () => document.removeEventListener('pointerdown', onDocDown, true);
    }, [visible, hide]);

    const onMouseEnter = () => {
      if (!(typeof window !== 'undefined' && 'ontouchstart' in window)) show();
    };
    const onMouseLeave = () => {
      if (!(typeof window !== 'undefined' && 'ontouchstart' in window)) hide();
    };

    const onClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (visible) hide();
      else show();
    };

    return (
      <>
        <button
          ref={btnRef}
          type="button"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          aria-describedby={srId}
          aria-label={ariaLabel ?? 'Info'}
          className="flex h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
        >
          <Info className="h-4 w-4" />
        </button>
        <span id={srId} className="sr-only">
          {text}
        </span>
        {visible && (
          <div
            ref={tooltipRef}
            role="status"
            aria-hidden={false}
            className="pointer-events-none fixed z-50 rounded-md bg-yellow-300 px-3 py-1 text-sm text-black shadow-sm transition-opacity duration-150"
            style={{
              left: posLeft,
              top: posTop,
              transform: placeBelow
                ? 'translateX(-50%) translateY(8px)'
                : 'translateX(-50%) translateY(calc(-100% - 8px))',
              maxWidth: 240,
            }}
          >
            {text}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <audio
        ref={musicPreviewAudioRef}
        onEnded={() => setPlayingMusicPreview(null)}
        // Default preview volume: quieter than session music
        onCanPlay={() => {
          try {
            if (musicPreviewAudioRef.current)
              musicPreviewAudioRef.current.volume = 0.25;
          } catch (e) {
            // ignore
          }
        }}
      />
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
        {/* Animated background gradient overlay */}
        <div
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
          style={{ animationDuration: '8s' }}
        />

        <div className="mx-auto max-w-5xl pt-10 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Start calming your mind and body
          </h1>
          <p className="text-lg text-gray-600">
            Create your perfect breathing session with customizable rhythms,
            gentle visuals, and real‑time guidance—then track your progress,
            build streaks, and stay motivated with inspiring stats and community
            insights.
          </p>
        </div>

        <div className="animate-fade-in relative mx-auto max-w-7xl px-4 py-12">
          <div className="grid gap-8 lg:grid-cols-3">
            {/* Main Breathing Control */}
            <div className="lg:col-span-2">
              {/* Quick Start Presets (full-width section, visually distinct) */}
              <div className="mb-8">
                <div className="card">
                  <h2 className="gradient-text mb-4 pb-4 text-center text-3xl font-bold sm:pb-6 sm:text-4xl">
                    Start a Preset Session
                  </h2>
                  <div className="mb-4 flex flex-wrap items-baseline gap-2">
                    <span className="mb-2 block text-[17px] font-bold">
                      Select a preset and begin breathing instantly.
                    </span>
                  </div>

                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <select
                      id="quick-preset-select"
                      value={selectedQuickPresetId}
                      onFocus={() => logDropdownOpen('preset')}
                      onPointerDown={() => logDropdownOpen('preset')}
                      onChange={(e) => handleQuickPresetSelect(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 md:flex-1"
                    >
                      <option value="">Choose a preset…</option>
                      {QUICK_PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label} — {formatSecondsLabel(p.duration)}
                        </option>
                      ))}
                    </select>
                    {selectedQuickPreset?.description ? (
                      <p className="mt-2 text-sm text-gray-600 md:ml-4 md:mt-0">
                        {selectedQuickPreset.description}
                      </p>
                    ) : null}
                    {selectedQuickPreset ? (
                      <div className="flex w-full items-center gap-3 md:w-auto">
                        <Button
                          type="button"
                          variant="primary"
                          size="lg"
                          onClick={startQuickPreset}
                          disabled={isBreathing}
                          className="begin-breathing-button w-full sm:w-auto"
                        >
                          Begin Breathing{' '}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div
                className="my-10 flex items-center justify-center"
                role="separator"
                aria-hidden
              >
                <hr className="flex-1 border-t border-gray-300 opacity-40" />
                <span className="mx-6 text-3xl font-semibold text-gray-600 opacity-90 sm:text-4xl">
                  OR
                </span>
                <hr className="flex-1 border-t border-gray-300 opacity-40" />
              </div>

              <div ref={customizeSessionRef} className="card mb-8">
                <h2 className="gradient-text mb-4 pb-6 text-center text-4xl font-bold">
                  Customize Your Session
                </h2>

                {/* Templates Dropdown (if any) */}
                {templates.length > 0 && (
                  <div className="mb-6">
                    <label
                      htmlFor="template-select"
                      className="mb-2 block text-[17px] font-bold"
                    >
                      Select Saved Template
                      <Link
                        to="/templates"
                        className="ml-2 text-sm font-semibold text-primary-600 transition hover:text-primary-700"
                      >
                        (Manage)
                      </Link>
                    </label>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                      <select
                        id="template-select"
                        value={selectedTemplateId}
                        onChange={(e) => handleSelectTemplate(e.target.value)}
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Choose a template…</option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.template_name}
                          </option>
                        ))}
                      </select>

                      <Button
                        type="button"
                        size="lg"
                        variant="primary"
                        className="begin-breathing-button w-full sm:w-auto"
                        onClick={() => startTemplateSession(selectedTemplateId)}
                        disabled={!selectedTemplateId}
                        title={
                          !selectedTemplateId
                            ? 'Select a template to start'
                            : 'Start a session with the selected template'
                        }
                      >
                        Begin Breathing
                      </Button>
                    </div>

                    <GradientDivider fullWidth />
                  </div>
                )}

                {/* Breathing Style Selector */}
                <div className="mb-6">
                  <label className="px-1 pb-2 text-[17px] font-bold">
                    Breathing Style
                  </label>
                  {breathingStyleDisplayOptions.length > 0 ? (
                    <div
                      className="grid grid-cols-1 gap-3 pt-5 sm:grid-cols-2"
                      onMouseDownCapture={() =>
                        logDropdownOpen('breathing_style')
                      }
                      onFocusCapture={() => logDropdownOpen('breathing_style')}
                    >
                      {breathingStyleDisplayOptions.map((style) => {
                        const isSelected = style.value === breathingStyle;
                        return (
                          <label
                            key={style.id}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-shadow hover:shadow sm:items-center ${
                              isSelected
                                ? 'breathing-selected-custom item-selected'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <input
                              type="radio"
                              name="breathingStyle"
                              value={style.value}
                              checked={isSelected}
                              onChange={() =>
                                handleBreathingStyleChange(
                                  style.value,
                                  style.label
                                )
                              }
                              className="mt-1 h-4 w-4 flex-shrink-0"
                            />
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <div
                                  className={`font-medium ${
                                    isSelected ? 'text-white' : 'text-gray-900'
                                  }`}
                                >
                                  {style.label}
                                </div>
                              </div>
                              {style.description ? (
                                <p
                                  className={`text-sm ${
                                    isSelected
                                      ? 'text-white/80'
                                      : 'text-gray-500'
                                  }`}
                                >
                                  {style.description}
                                </p>
                              ) : null}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="pt-5 text-sm text-gray-600">
                      No breathing styles are available right now.
                    </p>
                  )}
                </div>

                <GradientDivider fullWidth />

                {/* Ratio Selection (radio list with name + description) */}
                <div className="mb-6">
                  <label className="px-1 pb-2 text-[17px] font-bold">
                    Select Breathing Ratio
                  </label>

                  {breathingRatioOptions && breathingRatioOptions.length > 0 ? (
                    <div
                      className="grid grid-cols-1 gap-3 pt-5 sm:grid-cols-2"
                      onMouseDownCapture={() => logDropdownOpen('ratio')}
                      onFocusCapture={() => logDropdownOpen('ratio')}
                    >
                      {breathingRatioOptions.map((r) => {
                        const isSelected = selectedBreathingRatioId === r.id;
                        const isMostPopular =
                          mostPopularBreathingRatioId === r.id;
                        return (
                          <label
                            key={r.id}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-shadow hover:shadow sm:items-center ${
                              isSelected
                                ? 'breathing-selected-custom item-selected'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <input
                              type="radio"
                              name="breathingRatio"
                              value={r.id}
                              checked={isSelected}
                              onChange={() => handleBreathingRatioSelect(r)}
                              className="mt-1 h-4 w-4 flex-shrink-0"
                            />
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <div
                                  className={`font-medium ${
                                    isSelected ? 'text-white' : 'text-gray-900'
                                  }`}
                                >
                                  {r.name}
                                </div>
                                {isMostPopular ? (
                                  <span
                                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                      isSelected
                                        ? 'bg-white/20 text-white'
                                        : 'bg-amber-100 text-amber-700'
                                    }`}
                                  >
                                    Most Popular
                                  </span>
                                ) : null}
                              </div>
                              {r.description ? (
                                <div
                                  className={`mt-1 text-sm ${
                                    isSelected
                                      ? 'text-white/90'
                                      : 'text-gray-600'
                                  }`}
                                >
                                  {r.description}
                                </div>
                              ) : null}
                            </div>
                          </label>
                        );
                      })}

                      {/* Other / custom option */}
                      <label
                        className={`flex items-start gap-3 rounded-xl border p-3 transition-all ${
                          hasCustomRatioFeature
                            ? 'cursor-pointer hover:shadow'
                            : LOCKED_OPTION_BASE_CLASSES
                        } ${
                          customRatioSelected
                            ? 'breathing-selected-custom item-selected'
                            : hasCustomRatioFeature
                              ? 'border-gray-200 bg-white'
                              : 'border-amber-200 text-gray-800'
                        }`}
                        role={!hasCustomRatioFeature ? 'button' : undefined}
                        tabIndex={!hasCustomRatioFeature ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (
                            !hasCustomRatioFeature &&
                            (event.key === 'Enter' || event.key === ' ')
                          ) {
                            event.preventDefault();
                            logOptionSelect('ratio', 'custom', {
                              locked: true,
                              source: 'ratio_selector',
                            });
                            openCustomRatioModal();
                          }
                        }}
                        onClick={(event) => {
                          if (!hasCustomRatioFeature) {
                            event.preventDefault();
                            logOptionSelect('ratio', 'custom', {
                              locked: true,
                              source: 'ratio_selector',
                            });
                            openCustomRatioModal();
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="breathingRatio"
                          value="custom"
                          checked={customRatioSelected}
                          disabled={!hasCustomRatioFeature}
                          onChange={() => {
                            if (!hasCustomRatioFeature) {
                              openCustomRatioModal();
                              return;
                            }
                            handleCustomRatioSelection();
                          }}
                          className="mt-1 h-4 w-4 flex-shrink-0"
                          tabIndex={hasCustomRatioFeature ? undefined : -1}
                        />
                        <div className="flex-1">
                          <div
                            className={`font-medium ${
                              customRatioSelected
                                ? 'text-white'
                                : hasCustomRatioFeature
                                  ? 'text-gray-900'
                                  : 'text-gray-800'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              Other (Custom Ratio)
                            </span>
                          </div>
                          {hasCustomRatioFeature ? (
                            <>
                              <div
                                className={`mt-1 text-sm ${
                                  customRatioSelected
                                    ? 'text-white/90'
                                    : 'text-gray-600'
                                }`}
                              >
                                Use a custom ratio or a preset not listed above.
                              </div>
                              {customRatioSelected && (
                                <div className="mt-4 flex flex-wrap items-end gap-4">
                                  {CUSTOM_RATIO_LABELS.map((label, index) => (
                                    <div
                                      key={label}
                                      className="flex flex-col gap-1 text-sm"
                                    >
                                      <span className="text-xs font-medium text-white/80">
                                        {label}
                                      </span>
                                      <input
                                        type="number"
                                        min={0}
                                        max={60}
                                        step={1}
                                        inputMode="numeric"
                                        value={customRatioParts[index]}
                                        onChange={(e) => {
                                          const { value } = e.target;
                                          if (/^\d*$/.test(value)) {
                                            handleCustomRatioPartChange(
                                              index,
                                              value,
                                              'input'
                                            );
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const val = (
                                            e.target as HTMLInputElement
                                          ).value.trim();
                                          if (val === '') return;
                                          const num = Number(val);
                                          if (!Number.isFinite(num)) return;
                                          let clamped = Math.trunc(num);
                                          if (clamped < 0) clamped = 0;
                                          if (clamped > 60) clamped = 60;
                                          if (String(clamped) !== val) {
                                            handleCustomRatioPartChange(
                                              index,
                                              String(clamped),
                                              'blur'
                                            );
                                          }
                                        }}
                                        className="override-item-selected w-16 rounded-lg border border-white/50 bg-white/90 px-2 py-2 text-center text-black focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        aria-label={`${label} seconds`}
                                      />
                                    </div>
                                  ))}
                                  {hasCustomRatioError && (
                                    <div className="w-full">
                                      <p className="text-sm text-red-100">
                                        Enter numbers between 0 and 60
                                        (inclusive) for each phase.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="mt-2 space-y-2 text-sm text-gray-600">
                              <p className="flex items-center gap-2 font-medium text-primary-700">
                                Tap to learn how to unlock this feature.
                              </p>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-white/60 px-4 py-6 text-sm text-gray-600">
                      No breathing ratios are available yet. Please add a ratio
                      in the admin dashboard to start a session.
                    </div>
                  )}
                </div>

                <GradientDivider fullWidth />

                {/* Duration Selection */}
                <div className="mb-6">
                  <label className="mb-2 block text-[17px] font-bold">
                    How long would you like to breathe?
                  </label>
                  <div className="flex flex-wrap gap-4 pt-2">
                    {DURATION_OPTIONS.map((min) => {
                      const presetDisabled =
                        minimumDurationSeconds > 0 &&
                        min * 60 + 1e-6 < minimumDurationSeconds;

                      return (
                        <div
                          key={min}
                          className="flex w-auto flex-col items-center"
                        >
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              selectedDuration === min && !isCustomDuration
                                ? 'primary'
                                : 'secondary'
                            }
                            aria-pressed={
                              selectedDuration === min && !isCustomDuration
                            }
                            onClick={() => handleDurationSelect(min)}
                            disabled={presetDisabled}
                            title={
                              presetDisabled && minimumDurationLabel
                                ? `Minimum session length is ${minimumDurationLabel}.`
                                : undefined
                            }
                          >
                            {min} min
                          </Button>

                          {totalCycles > 0 &&
                            selectedDuration === min &&
                            !isCustomDuration && (
                              <span className="mt-2 text-xs text-gray-700">
                                {totalCycles} cycles
                              </span>
                            )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3 flex w-full">
                    <div className="flex w-auto flex-col items-center">
                      {hasCustomTimeFeature ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant={isCustomDuration ? 'primary' : 'secondary'}
                            aria-pressed={isCustomDuration}
                            onClick={handleCustomDurationButton}
                          >
                            <span className="flex items-center gap-2">
                              Custom Time
                            </span>
                          </Button>

                          {totalCycles > 0 && isCustomDuration && (
                            <span className="mt-2 text-xs text-gray-700">
                              {totalCycles} cycles
                            </span>
                          )}
                        </>
                      ) : (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => openCustomTimeModal()}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              logOptionSelect('duration', 'custom', {
                                locked: true,
                                source: 'duration_selector',
                              });
                              openCustomTimeModal();
                            }
                          }}
                          className={`mt-3 w-full rounded-xl px-4 py-3 text-center text-xs text-gray-700 ${LOCKED_OPTION_BASE_CLASSES}`}
                        >
                          <div className="flex justify-center">
                            <Button
                              type="button"
                              size="sm"
                              variant={
                                isCustomDuration ? 'primary' : 'secondary'
                              }
                              aria-pressed={isCustomDuration}
                              className={
                                LOCKED_OPTION_BASE_CLASSES + ' text-gray-800'
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                logOptionSelect('duration', 'custom', {
                                  locked: true,
                                  source: 'duration_selector',
                                });
                                openCustomTimeModal();
                              }}
                            >
                              <span className="flex items-center gap-2">
                                Custom Time
                              </span>
                            </Button>
                          </div>

                          <p className="mt-3 flex items-center justify-center gap-2 font-semibold text-primary-700">
                            Tap to learn how to unlock this feature.{' '}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {isCustomDuration && (
                    <div className="mt-3 flex items-center gap-3">
                      <input
                        type="number"
                        min={
                          minimumDurationMinutesCeil > 0
                            ? minimumDurationMinutesCeil
                            : 0.1
                        }
                        step="any"
                        placeholder="Minutes"
                        value={customDuration}
                        onChange={(e) =>
                          handleCustomDurationChange(e.target.value)
                        }
                        className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        aria-invalid={isDurationBelowMinimum}
                        title={
                          minimumDurationLabel
                            ? `Minimum session length is ${minimumDurationLabel}.`
                            : undefined
                        }
                      />
                      <span className="text-sm text-gray-500">minutes</span>
                    </div>
                  )}
                  {minimumDurationLabel && (
                    <p
                      className={`mt-3 text-sm ${isDurationBelowMinimum ? 'text-red-600' : 'text-gray-600'}`}
                    >
                      {isDurationBelowMinimum
                        ? '. Increase the duration to include at least one full cycle.'
                        : ''}
                    </p>
                  )}
                </div>
                <GradientDivider fullWidth />

                {/* Video Options (moved before Audio Options) */}
                {/* Inline chooser: show previews directly under the control */}

                <div className="mb-6">
                  <div className="text-xl font-bold">Background Options</div>

                  <label className="mb-2 block pt-2 text-sm text-gray-600">
                    Select one background type for your breathing session.
                    Choose a video, a vision board, or use a color or gradient
                    background.{' '}
                  </label>

                  <div className="mt-6">
                    <div className="text-[17px] font-bold">
                      Video Backgrounds
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Animated video backgrounds
                    </div>
                  </div>

                  <div className="mt-4 max-h-[20rem] overflow-y-auto pr-1">
                    <div
                      ref={chooserRef}
                      role="radiogroup"
                      aria-label="Background videos"
                      className="grid auto-rows-[10rem] grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
                      onMouseDownCapture={() => logDropdownOpen('video')}
                      onFocusCapture={() => logDropdownOpen('video')}
                    >
                      {unlockedVideoEntries.map((entry) => {
                        const isSelected =
                          !selectedVisionBoardImage &&
                          backgroundVideo === entry.key;
                        const isMostPopularVideo =
                          entry.key !== 'none' &&
                          entry.key === mostPopularBackgroundVideoKey;
                        const { displayName } =
                          getBackgroundVideoMetadata(entry);

                        const previewThumbnail = renderVideoPreview(
                          entry.key,
                          displayName,
                          isMostPopularVideo
                        );

                        return (
                          <label
                            key={entry.key}
                            className={`flex h-full w-full cursor-pointer flex-col items-center gap-2 rounded-lg border p-2 text-center transition-shadow hover:shadow-md ${
                              isSelected ? 'item-selected' : ''
                            }`}
                          >
                            {previewThumbnail}

                            <div
                              className={`flex w-full flex-wrap items-center justify-center gap-2 text-sm ${
                                isSelected ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              <input
                                type="radio"
                                name="backgroundVideo"
                                value={entry.key}
                                checked={isSelected}
                                onChange={() => {
                                  setHasTouchedBackgroundVideo(true);
                                  setIsColorBackgroundSelected(false);
                                  setIsGradientBackgroundSelected(false);
                                  setBackgroundVideo(entry.key);
                                  setSelectedVisionBoardImage(null);
                                  const found = backgroundVideosFromDb.find(
                                    (b) =>
                                      keyFromFileName(b.file_name) === entry.key
                                  );
                                  setSelectedBackgroundVideoId(
                                    found ? found.id : null
                                  );
                                  if (
                                    wishToEnableBackgroundSound &&
                                    entry.key !== 'none'
                                  ) {
                                    setBackgroundSound('on');
                                  }
                                  setWishToEnableBackgroundSound(false);
                                  logOptionSelect('video', entry.key, {
                                    displayName,
                                  });
                                }}
                                className="h-4 w-4"
                              />
                              <span className="truncate">{displayName}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {lockedVideoEntries.length > 0 && (
                      <div className="mt-6">
                        <p className="text-sm text-gray-600">
                          Tap to learn how to unlock any of these backgrounds.
                        </p>
                        <div
                          className="mt-4 grid auto-rows-[10rem] grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
                          onMouseDownCapture={() => logDropdownOpen('video')}
                          onFocusCapture={() => logDropdownOpen('video')}
                        >
                          {lockedVideoEntries.map((entry) => {
                            const isMostPopularVideo =
                              entry.key !== 'none' &&
                              entry.key === mostPopularBackgroundVideoKey;
                            const { displayName } =
                              getBackgroundVideoMetadata(entry);
                            const previewThumbnail = renderVideoPreview(
                              entry.key,
                              displayName,
                              isMostPopularVideo
                            );

                            return (
                              <div
                                key={entry.key}
                                role="button"
                                tabIndex={0}
                                aria-label={`Learn how to unlock ${displayName}`}
                                onClick={() => {
                                  logOptionSelect('video', entry.key, {
                                    displayName,
                                    locked: true,
                                  });
                                  handleLockedVideoClick(entry);
                                }}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                  ) {
                                    event.preventDefault();
                                    logOptionSelect('video', entry.key, {
                                      displayName,
                                      locked: true,
                                    });
                                    handleLockedVideoClick(entry);
                                  }
                                }}
                                className={`relative flex h-full w-full flex-col items-stretch gap-2 rounded-lg p-2 text-left ${LOCKED_OPTION_BASE_CLASSES}`}
                              >
                                {previewThumbnail}
                                <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                  <div className="font-bold">{displayName}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-8">
                    <div className="flex flex-wrap items-baseline gap-2 text-[17px] font-bold">
                      <span className="font-bold">Vision Boards</span>
                      <Link
                        to="/vision-boards"
                        className="ml-2 text-sm font-semibold text-primary-600 transition hover:text-primary-700"
                      >
                        (Manage)
                      </Link>
                    </div>
                    {visionBoardImages.length === 0 ? (
                      <div className="mt-3 rounded-lg border border-dashed border-gray-200 bg-white/60 px-4 py-6 text-center text-sm text-gray-600">
                        <div>You haven't saved any vision boards yet.</div>
                        <div className="mt-1 text-xs text-gray-500">
                          Create one to use it as a breathing background.
                        </div>
                        <div className="mt-3 flex justify-center">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="item-selected"
                            onClick={() => navigate('/vision-board')}
                          >
                            Create a Vision Board
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                        {visionBoardImages.map((image) => {
                          const isSelected =
                            selectedVisionBoardImage?.id === image.id;
                          const displayTitle =
                            image.title?.trim() || 'Untitled Vision Board';

                          return (
                            <label
                              key={image.id}
                              className={`flex h-full w-full cursor-pointer flex-col items-center gap-2 rounded-lg border p-2 text-center transition-shadow hover:shadow-md ${
                                isSelected ? 'item-selected' : ''
                              }`}
                            >
                              <div className="relative h-28 w-full overflow-hidden rounded bg-gray-50">
                                <img
                                  src={
                                    image.preview_image_url ??
                                    image.image_url ??
                                    ''
                                  }
                                  alt={displayTitle}
                                  loading="lazy"
                                  className="h-full w-full object-cover"
                                />
                                {image.is_template && (
                                  <div className="absolute right-1 top-1">
                                    <span className="rounded-full bg-primary-500/90 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm backdrop-blur-sm">
                                      Template
                                    </span>
                                  </div>
                                )}
                                {!image.is_template && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveVisionBoardToDelete(image);
                                      setDeleteVisionBoardError(null);
                                    }}
                                    title="Delete vision board"
                                    aria-label={`Delete ${displayTitle}`}
                                    className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-rose-600 shadow hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-200"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                )}
                              </div>
                              <div className="flex w-full items-center justify-center gap-2 text-sm">
                                <input
                                  type="radio"
                                  name="backgroundImage"
                                  value={image.id}
                                  checked={isSelected}
                                  onChange={() => {
                                    setHasTouchedBackgroundVideo(true);
                                    setIsColorBackgroundSelected(false);
                                    setIsGradientBackgroundSelected(false);
                                    setSelectedVisionBoardImage(image);
                                    setBackgroundVideo('none');
                                    setSelectedBackgroundVideoId(null);
                                    if (wishToEnableBackgroundSound) {
                                      setBackgroundSound('on');
                                    }
                                    setWishToEnableBackgroundSound(false);
                                    logOptionSelect('vision_board', image.id, {
                                      imageUrl: image.image_url,
                                      title: image.title,
                                      isTemplate: image.is_template,
                                    });
                                  }}
                                  className="h-4 w-4"
                                  aria-label={`Select ${displayTitle}`}
                                />
                                <span className="truncate font-medium text-gray-900">
                                  {displayTitle}
                                </span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {activeVisionBoardToDelete && (
                    <Modal
                      title="Delete Vision Board"
                      onClose={() => setActiveVisionBoardToDelete(null)}
                    >
                      <p className="text-sm text-gray-700">
                        Are you sure you want to delete this vision board? This
                        action cannot be undone.
                      </p>
                      {deleteVisionBoardError && (
                        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                          {deleteVisionBoardError}
                        </div>
                      )}
                      <div className="mt-6 flex items-center justify-end gap-3">
                        <Button
                          type="button"
                          onClick={() => setActiveVisionBoardToDelete(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={handleDeleteVisionBoard}
                          disabled={isDeletingVisionBoard}
                          className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                        >
                          {isDeletingVisionBoard ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    </Modal>
                  )}

                  <div className="mt-8">
                    <div className="text-[17px] font-bold">
                      Color &amp; Gradient Background
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Use a solid color or a calming gradient background.
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-4 text-sm font-semibold text-slate-700">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="colorBackgroundMode"
                          value="custom"
                          checked={colorBackgroundMode === 'custom'}
                          onChange={() =>
                            applyColorBackgroundSelection(
                              backgroundColor,
                              'picker'
                            )
                          }
                          className="h-4 w-4"
                        />
                        <span>Custom</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="colorBackgroundMode"
                          value="gradient"
                          checked={colorBackgroundMode === 'gradient'}
                          onChange={() =>
                            applyGradientBackgroundSelection(
                              backgroundGradient,
                              'preset'
                            )
                          }
                          className="h-4 w-4"
                        />
                        <span>Gradient</span>
                      </label>
                    </div>

                    {colorBackgroundMode === 'custom' ? (
                      <>
                        <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Custom Color
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-4">
                          <label className="flex items-center gap-3 text-sm font-medium text-gray-700">
                            <span>Select</span>
                            <input
                              type="color"
                              aria-label="Choose a background color"
                              value={backgroundColor}
                              onChange={(event) =>
                                applyColorBackgroundSelection(
                                  event.target.value,
                                  'picker'
                                )
                              }
                              className={`h-10 w-12 cursor-pointer rounded border ${
                                isColorBackgroundSelected
                                  ? 'border-primary-400 shadow-sm'
                                  : 'border-gray-300'
                              }`}
                            />
                          </label>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span
                              aria-hidden="true"
                              className={`h-8 w-8 rounded-md border ${
                                isColorBackgroundSelected
                                  ? 'border-primary-400 ring-2 ring-primary-200'
                                  : 'border-gray-200'
                              }`}
                              style={{ backgroundColor }}
                            />
                            <span className="font-mono text-xs">
                              {backgroundColor.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Preset Gradients
                        </div>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          {GRADIENT_BACKGROUND_PRESETS.map((preset) => {
                            const isActive =
                              isGradientBackgroundSelected &&
                              backgroundGradient === preset.value;
                            return (
                              <button
                                key={preset.id}
                                type="button"
                                onClick={() =>
                                  applyGradientBackgroundSelection(
                                    preset.value,
                                    'preset'
                                  )
                                }
                                className={`relative h-16 w-full overflow-hidden rounded-2xl border transition ${
                                  isActive
                                    ? 'border-white ring-2 ring-primary-400 ring-offset-2'
                                    : 'border-white/70 hover:shadow-sm'
                                }`}
                                style={{ backgroundImage: preset.value }}
                                aria-label={`Select ${preset.label}`}
                              >
                                <span className="absolute bottom-2 left-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-slate-900 shadow-sm">
                                  {preset.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <GradientDivider fullWidth />

                {/* Audio Options */}
                <div className="mb-6">
                  <div className="text-[17px] font-bold ">Audio Options</div>
                  <div className="mt-2 pt-2">
                    {/* Affirmations */}
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                        <span>Affirmations</span>
                        <InfoTooltip
                          text={
                            'Plays positive spoken affirmations during the session to support mindset, intention, and emotional balance.'
                          }
                          ariaLabel="Affirmations info"
                        />
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={!affirmations ? 'primary' : 'secondary'}
                          aria-pressed={!affirmations}
                          onClick={() => handleAffirmationsToggle(false)}
                        >
                          Off
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={affirmations ? 'primary' : 'secondary'}
                          aria-pressed={affirmations}
                          onClick={() => {
                            handleAffirmationsToggle(true);
                            if (!affirmationType) {
                              setAffirmationType(DEFAULT_AFFIRMATION_TYPE);
                            }
                            // Only force Voice Guidance on if we are in subliminal mode.
                            // If we are in regular mode, it's okay to have both or either.
                            if (affirmationWhisperMode) {
                              setVoiceGuidance(true);
                            }
                          }}
                        >
                          On
                        </Button>
                      </div>
                    </div>

                    {/* Affirmation Type */}
                    {affirmations && (
                      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <span className="text-sm text-gray-600 sm:w-40 sm:text-right">
                          Affirmation Type
                        </span>
                        <select
                          value={affirmationType}
                          onFocus={() => logDropdownOpen('affirmation')}
                          onPointerDown={() => logDropdownOpen('affirmation')}
                          onChange={(e) =>
                            handleAffirmationTypeChange(
                              e.target.value as AffirmationType
                            )
                          }
                          className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          {AFFIRMATION_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Affirmation Whisper Mode */}
                    {affirmations && (
                      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                          <span>Affirmation Volume</span>
                          <InfoTooltip
                            text={
                              'Controls how affirmations are played during the session. Regular is clearly audible. Subliminal plays the same audio at a very low volume for a subtle background effect.'
                            }
                            ariaLabel="Affirmation volume info"
                          />
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              !affirmationWhisperMode ? 'primary' : 'secondary'
                            }
                            aria-pressed={!affirmationWhisperMode}
                            onClick={() =>
                              handleAffirmationWhisperModeToggle(false)
                            }
                            className="flex items-center gap-2"
                          >
                            <Volume2 className="h-4 w-4" />
                            Regular
                          </Button>

                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={
                                affirmationWhisperMode ? 'primary' : 'secondary'
                              }
                              aria-pressed={affirmationWhisperMode}
                              onClick={() =>
                                handleAffirmationWhisperModeToggle(true)
                              }
                              className="flex items-center gap-2"
                            >
                              <VolumeX className="h-4 w-4" />
                              Subliminal
                            </Button>

                            <InfoTooltip
                              text={
                                'Uses the same affirmation audio, played at a very low volume for a subtle background effect.'
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Voice Guidance */}
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                        <span>Voice Guidance</span>
                        <InfoTooltip
                          text={
                            'Plays spoken guidance to lead you through the breathing session and keep you on rhythm.'
                          }
                          ariaLabel="Voice guidance info"
                        />
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={!voiceGuidance ? 'primary' : 'secondary'}
                          aria-pressed={!voiceGuidance}
                          onClick={() => handleVoiceGuidanceToggle(false)}
                          disabled={affirmations && !affirmationWhisperMode}
                          title={
                            affirmations && !affirmationWhisperMode
                              ? 'Enable Subliminal Affirmations to allow Voice Guidance'
                              : undefined
                          }
                        >
                          Off
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={voiceGuidance ? 'primary' : 'secondary'}
                          aria-pressed={voiceGuidance}
                          onClick={() => handleVoiceGuidanceToggle(true)}
                          disabled={affirmations && !affirmationWhisperMode}
                          title={
                            affirmations && !affirmationWhisperMode
                              ? 'Enable Subliminal Affirmations to allow Voice Guidance'
                              : undefined
                          }
                        >
                          On
                        </Button>
                      </div>
                    </div>

                    {/* Phase Chime */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                        <span>Phase Chime</span>
                        <InfoTooltip
                          text={
                            'Plays a gentle sound at each breathing phase change (inhale, hold, exhale) to guide timing without voice.'
                          }
                          ariaLabel="Phase chime info"
                        />
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={!phaseChime ? 'primary' : 'secondary'}
                          aria-pressed={!phaseChime}
                          onClick={() => handlePhaseChimeToggle(false)}
                        >
                          Off
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={phaseChime ? 'primary' : 'secondary'}
                          aria-pressed={phaseChime}
                          onClick={() => handlePhaseChimeToggle(true)}
                        >
                          On
                        </Button>
                      </div>
                    </div>

                    {/* Intro Voice */}
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                        <span>Intro Voice</span>
                        <InfoTooltip
                          text={
                            'Plays a short spoken introduction before the session starts to help you settle in and prepare.'
                          }
                          ariaLabel="Intro voice info"
                        />
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={!introVoice ? 'primary' : 'secondary'}
                          aria-pressed={!introVoice}
                          onClick={() => handleIntroVoiceToggle(false)}
                        >
                          Off
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={introVoice ? 'primary' : 'secondary'}
                          aria-pressed={introVoice}
                          onClick={() => handleIntroVoiceToggle(true)}
                        >
                          On
                        </Button>
                      </div>
                    </div>

                    {/* Ambiant Sound Selection (moved below Phase Chime) */}
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                        <span>Ambient Sound</span>
                        <InfoTooltip
                          text={
                            'Adds a calming background sound (like nature or atmosphere) to enhance relaxation during the session.'
                          }
                          ariaLabel="Ambient sound info"
                        />
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            backgroundSound === 'off' ? 'primary' : 'secondary'
                          }
                          aria-pressed={backgroundSound === 'off'}
                          onClick={() => handleBackgroundSoundToggle('off')}
                        >
                          Off
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            backgroundSound === 'on' ? 'primary' : 'secondary'
                          }
                          aria-pressed={backgroundSound === 'on'}
                          onClick={() => handleBackgroundSoundToggle('on')}
                          disabled={!hasSelectedBackground}
                          title={
                            !hasSelectedBackground
                              ? 'Select a background to enable Ambiant Sound'
                              : undefined
                          }
                        >
                          On
                        </Button>
                      </div>
                      {!hasSelectedBackground ? (
                        <button
                          type="button"
                          onClick={() => {
                            try {
                              if (chooserRef && chooserRef.current) {
                                chooserRef.current.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'center',
                                });
                              }
                            } catch (e) {
                              // ignore scrolling errors
                            }
                            setWishToEnableBackgroundSound(true);
                          }}
                          className="mt-2 text-sm text-primary-600 underline"
                          aria-label="Select background to enable sound"
                        >
                          Select a background below to enable sound
                        </button>
                      ) : null}
                    </div>

                    {/* Music Selection */}
                    <div className="mt-4 flex flex-col gap-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                          <span>Music</span>
                          <InfoTooltip
                            text={
                              'Plays background music during the session to create a deeper, more immersive experience.'
                            }
                            ariaLabel="Music info"
                          />
                        </span>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={!musicEnabled ? 'primary' : 'secondary'}
                            aria-pressed={!musicEnabled}
                            onClick={() => handleMusicToggle(false)}
                          >
                            Off
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={musicEnabled ? 'primary' : 'secondary'}
                            aria-pressed={musicEnabled}
                            onClick={() => handleMusicToggle(true)}
                          >
                            On
                          </Button>
                        </div>
                      </div>

                      {musicEnabled && (
                        <div className="mt-4 flex flex-col gap-3 pl-0 sm:pl-40">
                          <div className="flex flex-col gap-3">
                            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                              Choose a track
                            </span>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              {musicTracks.length === 0 ? (
                                <p className="col-span-2 text-sm text-gray-500">
                                  No music tracks available.
                                </p>
                              ) : (
                                musicTracks.map((track) => {
                                  const isSelected =
                                    selectedMusicTrackId === track.id;
                                  return (
                                    <div
                                      key={track.id}
                                      className={`relative flex items-center justify-between gap-3 rounded-lg border p-3 transition-all ${
                                        isSelected
                                          ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                                          : 'border-gray-200 bg-white hover:border-indigo-300'
                                      }`}
                                    >
                                      <label className="flex flex-1 cursor-pointer items-center gap-3">
                                        <input
                                          type="radio"
                                          name="selectedMusicTrack"
                                          checked={isSelected}
                                          onChange={() =>
                                            handleMusicTrackSelect(track.id)
                                          }
                                          className="h-4 w-4 text-indigo-600"
                                        />
                                        <span
                                          className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-700'}`}
                                        >
                                          {track.name}
                                        </span>
                                      </label>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          toggleMusicPreview(track.audio_url);
                                        }}
                                        className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                                          playingMusicPreview ===
                                          track.audio_url
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                                        }`}
                                        title="Preview track"
                                      >
                                        {playingMusicPreview ===
                                        track.audio_url ? (
                                          <svg
                                            className="h-4 w-4"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                          </svg>
                                        ) : (
                                          <svg
                                            className="h-4 w-4"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path d="M8 5v14l11-7z" />
                                          </svg>
                                        )}
                                      </button>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <GradientDivider fullWidth />

                {/* Other Options */}
                <div className="mt-8">
                  <label className="mb-2 block text-[17px] font-bold">
                    Other Options
                  </label>
                  <div className="rounded-2xl p-4 sm:p-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                        <span>Background Play</span>
                        <InfoTooltip
                          text={
                            'Allows the breathing session to continue running even if you switch tabs or your screen turns off.'
                          }
                          ariaLabel="Background play info"
                        />
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={!playInBackground ? 'primary' : 'secondary'}
                          aria-pressed={!playInBackground}
                          onClick={() => handlePlayInBackgroundToggle(false)}
                        >
                          Off
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={playInBackground ? 'primary' : 'secondary'}
                          aria-pressed={playInBackground}
                          onClick={() => handlePlayInBackgroundToggle(true)}
                        >
                          On
                        </Button>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                        <span>Cycle Count</span>
                        <InfoTooltip
                          text={
                            'Shows the cycle counter during the breathing session.'
                          }
                          ariaLabel="Cycle count info"
                        />
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={hideCycleCount ? 'primary' : 'secondary'}
                          aria-pressed={hideCycleCount}
                          onClick={() => handleHideCycleCountToggle(true)}
                        >
                          Off
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={!hideCycleCount ? 'primary' : 'secondary'}
                          aria-pressed={!hideCycleCount}
                          onClick={() => handleHideCycleCountToggle(false)}
                        >
                          On
                        </Button>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="flex items-center gap-2 text-sm text-gray-600 sm:w-40 sm:justify-end sm:text-right">
                        <span>Time Left</span>
                        <InfoTooltip
                          text={
                            'Shows the remaining time display during the breathing session.'
                          }
                          ariaLabel="Time left info"
                        />
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={hideTimeLeft ? 'primary' : 'secondary'}
                          aria-pressed={hideTimeLeft}
                          onClick={() => handleHideTimeLeftToggle(true)}
                        >
                          Off
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={!hideTimeLeft ? 'primary' : 'secondary'}
                          aria-pressed={!hideTimeLeft}
                          onClick={() => handleHideTimeLeftToggle(false)}
                        >
                          On
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <GradientDivider fullWidth />

                {/* Save Template (before Begin Breathing) */}
                <div className="mb-4">
                  <label className="mb-2 block text-[17px] font-bold">
                    <div className="flex items-center gap-2">
                      <span>Save current settings as a template</span>
                      <InfoTooltip
                        text={
                          'Saves your current breathing, audio, and visual settings so you can quickly reuse them later.'
                        }
                        ariaLabel="Save template info"
                      />
                    </div>
                  </label>
                  <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                    <input
                      type="text"
                      placeholder="Template name (e.g., Evening Calm)"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <Button
                      type="button"
                      className="item-selected"
                      variant="secondary"
                      onClick={handleSaveTemplate}
                      disabled={
                        isSavingTemplate ||
                        !templateName.trim() ||
                        isDurationBelowMinimum ||
                        !isCustomRatioReady ||
                        !hasRatioForSession
                      }
                      title={
                        isDurationBelowMinimum && minimumDurationLabel
                          ? `Increase the duration to at least ${minimumDurationLabel} before saving.`
                          : undefined
                      }
                    >
                      {isSavingTemplate ? 'Saving…' : 'Save Template'}
                    </Button>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleStartBreathing}
                    disabled={
                      isBreathing ||
                      isDurationBelowMinimum ||
                      !isCustomRatioReady ||
                      !hasRatioForSession
                    }
                    className="begin-breathing-button flex-1"
                    title={
                      isDurationBelowMinimum && minimumDurationLabel
                        ? `Increase the duration to at least ${minimumDurationLabel} to begin.`
                        : undefined
                    }
                  >
                    {isBreathing ? 'In Session...' : 'Begin Breathing'}
                  </Button>
                </div>
              </div>

              {showCustomRatioUnlockModal &&
                (() => {
                  const single =
                    customRatioRequiredBadgeObjects.length === 1
                      ? customRatioRequiredBadgeObjects[0]
                      : null;

                  return (
                    <Modal
                      title="Unlock Custom Breathing Ratios"
                      onClose={closeCustomRatioModal}
                      className="max-w-xl"
                      {...(single
                        ? {
                            style: {
                              backgroundColor: single.color || undefined,
                            },
                            textColorClass: single.color
                              ? 'text-white'
                              : undefined,
                            closeButtonClass: single.color
                              ? 'text-white transition hover:bg-white/10 hover:text-white'
                              : undefined,
                          }
                        : undefined)}
                    >
                      {single ? (
                        <div className="flex justify-center">
                          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                            <div className="flex items-center gap-6 sm:gap-10">
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setPreviewBadge(single)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPreviewBadge(single);
                                  }
                                }}
                                className="h-56 w-56 flex-shrink-0 cursor-pointer overflow-hidden rounded-full border border-gray-100 shadow-inner"
                                style={{
                                  backgroundColor: single.color ?? undefined,
                                }}
                              >
                                {single.image_url ? (
                                  <img
                                    src={
                                      buildBadgeAssetUrl(single.image_url) ||
                                      undefined
                                    }
                                    alt={single.name || 'badge'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-3xl">
                                    {single.name?.slice(0, 1) ?? '🏅'}
                                  </div>
                                )}
                              </div>

                              <div className="flex-1 text-left">
                                {single.name && (
                                  <div className="text-2xl font-semibold text-gray-900">
                                    {single.name}
                                  </div>
                                )}
                                <div className="mt-4 text-sm text-gray-700">
                                  <p>{customRatioUnlockMessage}</p>
                                </div>
                              </div>
                            </div>

                            <div className="mt-6">
                              <Button
                                type="button"
                                className="item-selected w-full"
                                onClick={handleViewBadges}
                              >
                                View All Badges
                              </Button>
                            </div>

                            {/* removed duplicate customTime block (was accidentally inserted) */}
                          </div>
                        </div>
                      ) : customRatioRequiredBadgeObjects.length > 0 ? (
                        <div className="flex justify-center gap-3">
                          {customRatioRequiredBadgeObjects.map((badge) => (
                            <div
                              key={badge.id || badge.name}
                              className="flex flex-col items-center"
                            >
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setPreviewBadge(badge)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPreviewBadge(badge);
                                  }
                                }}
                                className="h-48 w-48 cursor-pointer overflow-hidden rounded-full focus:outline-none focus:ring-2"
                                style={{
                                  backgroundColor: badge.color ?? undefined,
                                }}
                              >
                                {badge.image_url ? (
                                  <img
                                    src={
                                      buildBadgeAssetUrl(badge.image_url) ||
                                      undefined
                                    }
                                    alt={badge.name || 'badge'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-xl">
                                    {badge.name?.slice(0, 1) ?? '🏅'}
                                  </div>
                                )}
                              </div>
                              {badge.name && (
                                <div
                                  className="mt-2 text-xs font-semibold"
                                  style={{
                                    color: badge.color ? '#fff' : undefined,
                                  }}
                                >
                                  {badge.name}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {single ? null : (
                        <>
                          <p className="text-sm text-gray-600">
                            {customRatioUnlockMessage}
                          </p>
                          <Button
                            type="button"
                            className="item-selected mt-6 w-full"
                            onClick={handleViewBadges}
                          >
                            View All Badges
                          </Button>
                        </>
                      )}
                    </Modal>
                  );
                })()}

              {showCustomTimeLockedModal &&
                (() => {
                  const single =
                    customTimeRequiredBadgeObjects.length === 1
                      ? customTimeRequiredBadgeObjects[0]
                      : null;

                  return (
                    <Modal
                      title="Custom Session Time Locked"
                      onClose={closeCustomTimeModal}
                      className="max-w-xl"
                      {...(single
                        ? {
                            style: {
                              backgroundColor: single.color || undefined,
                            },
                            textColorClass: single.color
                              ? 'text-white'
                              : undefined,
                            closeButtonClass: single.color
                              ? 'text-white transition hover:bg-white/10 hover:text-white'
                              : undefined,
                          }
                        : undefined)}
                    >
                      {single ? (
                        <div className="flex justify-center">
                          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                            <div className="flex items-center gap-6 sm:gap-10">
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setPreviewBadge(single)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPreviewBadge(single);
                                  }
                                }}
                                className="h-56 w-56 flex-shrink-0 cursor-pointer overflow-hidden rounded-full border border-gray-100 shadow-inner"
                                style={{
                                  backgroundColor: single.color ?? undefined,
                                }}
                              >
                                {single.image_url ? (
                                  <img
                                    src={
                                      buildBadgeAssetUrl(single.image_url) ||
                                      undefined
                                    }
                                    alt={single.name || 'badge'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-3xl">
                                    {single.name?.slice(0, 1) ?? '🏅'}
                                  </div>
                                )}
                              </div>

                              <div className="flex-1 text-left">
                                {single.name && (
                                  <div className="text-2xl font-semibold text-gray-900">
                                    {single.name}
                                  </div>
                                )}
                                <div className="mt-4 text-sm text-gray-700">
                                  <p>{customTimeUnlockMessage}</p>
                                </div>
                              </div>
                            </div>

                            <div className="mt-6">
                              <Button
                                type="button"
                                className="item-selected w-full"
                                onClick={handleViewBadges}
                              >
                                View All Badges
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : customTimeRequiredBadgeObjects.length > 0 ? (
                        <div className="flex justify-center gap-3">
                          {customTimeRequiredBadgeObjects.map((badge) => (
                            <div
                              key={badge.id || badge.name}
                              className="flex flex-col items-center"
                            >
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setPreviewBadge(badge)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPreviewBadge(badge);
                                  }
                                }}
                                className="h-48 w-48 cursor-pointer overflow-hidden rounded-full focus:outline-none focus:ring-2"
                                style={{
                                  backgroundColor: badge.color ?? undefined,
                                }}
                              >
                                {badge.image_url ? (
                                  <img
                                    src={
                                      buildBadgeAssetUrl(badge.image_url) ||
                                      undefined
                                    }
                                    alt={badge.name || 'badge'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-xl">
                                    {badge.name?.slice(0, 1) ?? '🏅'}
                                  </div>
                                )}
                              </div>
                              {badge.name && (
                                <div
                                  className="mt-2 text-xs font-semibold"
                                  style={{
                                    color: badge.color ? '#fff' : undefined,
                                  }}
                                >
                                  {badge.name}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {single ? null : (
                        <>
                          <p className="text-sm text-gray-600">
                            {customTimeUnlockMessage}
                          </p>
                          <Button
                            type="button"
                            className="item-selected mt-6 w-full"
                            onClick={handleViewBadges}
                          >
                            View All Badges
                          </Button>
                        </>
                      )}
                    </Modal>
                  );
                })()}

              {/* Register prompt modal shown when unauthenticated user tries to save */}
              {showRegisterModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
                  <div
                    className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    onClick={() => setShowRegisterModal(false)}
                  />
                  <div
                    className="relative w-full max-w-md rounded-2xl bg-white/95 p-6 shadow-2xl backdrop-blur"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2 className="mb-2 text-lg font-semibold text-gray-900">
                      Create an account to save templates
                    </h2>
                    <p className="mb-4 text-sm text-gray-600">
                      Saving templates is available for registered users. Create
                      an account or sign in to save your custom settings for
                      later.
                    </p>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                      <Button
                        type="button"
                        variant="primary"
                        className="w-full sm:w-auto"
                        onClick={() => navigate('/signup')}
                      >
                        Create Account
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="w-full sm:w-auto"
                        onClick={() => navigate('/signin')}
                      >
                        Sign In
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => setShowRegisterModal(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {previewBadge && (
                <Modal
                  title={previewBadge.name ?? undefined}
                  onClose={() => setPreviewBadge(null)}
                  className="max-w-xl"
                  style={{ backgroundColor: previewBadge.color || undefined }}
                  textColorClass="text-white"
                  closeButtonClass="text-white transition hover:bg-white/10 hover:text-white"
                >
                  <div className="flex justify-center">
                    <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                      <div className="flex items-center gap-6 text-gray-900">
                        <div
                          className="h-56 w-56 flex-shrink-0 overflow-hidden rounded-full border border-gray-100 shadow-inner"
                          style={{
                            backgroundColor: previewBadge.color ?? undefined,
                          }}
                        >
                          {previewBadge.image_url ? (
                            <img
                              src={
                                buildBadgeAssetUrl(previewBadge.image_url) ||
                                undefined
                              }
                              alt={previewBadge.name ?? 'badge'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-3xl">
                              {previewBadge.name?.slice(0, 1) ?? '🏅'}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 text-left">
                          {previewBadge.name && (
                            <div className="text-2xl font-semibold text-gray-900">
                              {previewBadge.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Modal>
              )}

              {lockedVideoModalInfo && (
                <Modal
                  title={`${lockedVideoModalInfo.displayName} Locked`}
                  onClose={closeLockedVideoModal}
                  className="max-w-xl"
                  style={{
                    backgroundColor:
                      lockedVideoModalPrimaryBadge?.color ?? '#4f6ef5',
                  }}
                  textColorClass={
                    lockedVideoModalPrimaryBadge?.color
                      ? 'text-white'
                      : undefined
                  }
                  closeButtonClass={
                    lockedVideoModalPrimaryBadge?.color
                      ? 'text-white transition hover:bg-white/20 hover:text-white'
                      : undefined
                  }
                >
                  <div className="flex justify-center">
                    <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                      <div className="flex flex-col gap-6">
                        <div className="flex flex-col items-center gap-5 md:flex-row">
                          <div className="flex h-56 w-56 items-center justify-center rounded-full border border-gray-100 bg-white p-4 shadow-inner">
                            {lockedVideoModalPrimaryBadge?.image_url ? (
                              <img
                                src={
                                  buildBadgeAssetUrl(
                                    lockedVideoModalPrimaryBadge.image_url
                                  ) || undefined
                                }
                                alt={
                                  lockedVideoModalPrimaryBadge.name ?? 'badge'
                                }
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="text-4xl font-semibold text-gray-900">
                                {lockedVideoModalPrimaryBadge?.name?.slice(
                                  0,
                                  1
                                ) ?? '🏅'}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            {lockedVideoModalPrimaryBadge?.name ? (
                              <div className="text-2xl font-semibold text-gray-900">
                                {lockedVideoModalPrimaryBadge.name}
                              </div>
                            ) : (
                              <div className="text-2xl font-semibold text-gray-900">
                                {lockedVideoModalInfo.displayName}
                              </div>
                            )}
                            <p className="mt-2 text-sm text-gray-600">
                              {lockedVideoModalInfo.message}
                            </p>
                          </div>
                        </div>
                        {lockedVideoModalBadgeList.length > 1 && (
                          <div className="flex flex-wrap items-center gap-4">
                            {lockedVideoModalBadgeList.map((badge, index) => {
                              const badgeKey =
                                badge.id ??
                                badge.name ??
                                badge.image_url ??
                                `badge-${index}`;
                              return (
                                <div
                                  key={badgeKey}
                                  className="flex flex-col items-center gap-2 text-xs text-gray-700"
                                >
                                  <div
                                    className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white shadow-sm"
                                    style={{
                                      backgroundColor: badge.color ?? undefined,
                                    }}
                                  >
                                    {badge.image_url ? (
                                      <img
                                        src={
                                          buildBadgeAssetUrl(badge.image_url) ||
                                          undefined
                                        }
                                        alt={badge.name || 'badge'}
                                        className="h-full w-full object-cover"
                                      />
                                    ) : (
                                      <span className="text-base font-semibold text-gray-900">
                                        {badge.name?.slice(0, 1) ?? '🏅'}
                                      </span>
                                    )}
                                  </div>
                                  {badge.name && (
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                                      {badge.name}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <Button
                          type="button"
                          className="item-selected w-full"
                          onClick={handleViewBadges}
                        >
                          View All Badges
                        </Button>
                      </div>
                    </div>
                  </div>
                </Modal>
              )}

              {/* Recent Sessions or Sign-Up Prompt */}
              {!user ? (
                <div className="card flex flex-col">
                  <h2 className="gradient-text mb-4 pb-6 text-center text-4xl font-bold">
                    Recent Sessions
                  </h2>
                  <div className="flex flex-col items-center gap-6 py-8 text-center">
                    <div>
                      <p className="mb-3 text-lg font-semibold text-gray-900">
                        Want to see your breathing history?
                      </p>
                      <p className="text-gray-600">
                        Create a free account to save your sessions, follow your
                        progress, and join the{' '}
                        <span className="logo-font font-bold">Xhalin</span>{' '}
                        community.
                      </p>
                    </div>
                    <div className="w-full">
                      <Button
                        type="button"
                        variant="primary"
                        size="lg"
                        className="begin-breathing-button w-full"
                        onClick={() => navigate('/signup')}
                      >
                        Start My Journey
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card">
                  <h2 className="gradient-text mb-4 pb-6 text-center text-4xl font-bold">
                    Recent Sessions
                  </h2>
                  <div className="space-y-3">
                    {displaySessions.length > 0 ? (
                      <>
                        {/* Mobile-friendly card list (shown on small screens) */}
                        <div className="space-y-3 md:hidden">
                          {displaySessions.map((session) => {
                            const label =
                              formatSessionDateWithTimezone(session);
                            return (
                              <div
                                key={session.id}
                                className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline justify-between gap-2">
                                      <div className="truncate text-sm font-semibold text-gray-900">
                                        {session.breathing_ratio_name ||
                                          'Custom'}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {label.date} {label.time}
                                      </div>
                                    </div>

                                    {editingSessionId === session.id ? (
                                      <div className="mt-2 flex flex-col gap-2">
                                        <textarea
                                          rows={2}
                                          value={editingNotes}
                                          onChange={(e) =>
                                            setEditingNotes(e.target.value)
                                          }
                                          className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-white/70 px-3 py-2 text-sm text-slate-900 shadow-inner outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                                        />
                                        <div className="flex flex-wrap items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={saveEditingNotes}
                                            disabled={notesSaving}
                                            className="item-selected inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-sm font-semibold tracking-wider text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                                          >
                                            Save
                                          </button>
                                          <button
                                            type="button"
                                            onClick={cancelEditingNotes}
                                            disabled={notesSaving}
                                            className="item-selected inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-slate-400"
                                          >
                                            Cancel
                                          </button>
                                          {notesError && (
                                            <span className="text-xs text-red-500">
                                              {notesError}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ) : session.notes ? (
                                      <div className="mt-2 flex items-center gap-2 truncate text-xs text-gray-600">
                                        <span className="truncate">
                                          {session.notes}
                                        </span>
                                        <button
                                          type="button"
                                          title="Edit notes"
                                          onClick={() =>
                                            startEditingNotes(session)
                                          }
                                          className="-mr-1 ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-slate-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200"
                                        >
                                          <span
                                            aria-hidden="true"
                                            className="text-sm leading-none"
                                          >
                                            ✏️
                                          </span>
                                          <span className="sr-only">
                                            Edit notes
                                          </span>
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>

                                  <div className="flex flex-col items-end justify-between text-right">
                                    <div className="text-sm font-semibold text-primary-600">
                                      {session.minutesSeconds || ''}
                                    </div>
                                    <div className="mt-1 text-xs text-gray-700">
                                      Cycles: {session.cycles}
                                    </div>
                                    <div className="mt-2">
                                      <span
                                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                                          session.visibility === 'Public'
                                            ? 'border-green-200 bg-green-50 text-green-800'
                                            : 'border-red-200 bg-red-50 text-red-800'
                                        }`}
                                      >
                                        {session.visibility}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Desktop/table view (hidden on small screens) */}
                        <div className="mt-2 hidden overflow-x-auto rounded-xl border border-gray-100 md:block">
                          <table className="min-w-full text-left text-sm text-gray-800">
                            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                              <tr>
                                <th className="w-[16%] px-4 py-3">
                                  Date and Time
                                </th>
                                <th className="w-[18%] px-4 py-3">Breathing</th>
                                <th className="w-[10%] px-4 py-3">Duration</th>
                                <th className="w-[10%] px-4 py-3">Cycles</th>
                                <th className="px-4 py-3">Notes</th>
                                <th className="px-4 py-3 text-center">
                                  Privacy
                                </th>
                                {/* Removed 'View' column */}
                              </tr>
                            </thead>
                            <tbody>
                              {displaySessions.map((session) => {
                                const label =
                                  formatSessionDateWithTimezone(session);
                                return (
                                  <tr
                                    key={session.id}
                                    className="border-t border-gray-100 hover:bg-indigo-50"
                                  >
                                    <td className="px-4 py-3 align-top">
                                      <div className="font-semibold text-gray-900">
                                        {label.date}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {label.time}
                                      </div>
                                    </td>

                                    <td className="px-4 py-3 align-top">
                                      <div className="text-sm font-semibold text-gray-900">
                                        {session.breathing_ratio_name ||
                                          'Custom'}
                                      </div>
                                    </td>

                                    <td className="px-4 py-3 align-top text-sm font-semibold text-primary-600">
                                      {session.minutesSeconds || ''}
                                    </td>

                                    <td className="px-4 py-3 align-top text-sm text-gray-700">
                                      {session.cycles}
                                    </td>

                                    <td className="px-4 py-3 align-top">
                                      {editingSessionId === session.id ? (
                                        <div className="flex flex-col gap-2">
                                          <textarea
                                            rows={2}
                                            value={editingNotes}
                                            onChange={(e) =>
                                              setEditingNotes(e.target.value)
                                            }
                                            className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-white/70 px-3 py-2 text-sm text-slate-900 shadow-inner outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                                          />
                                          <div className="flex flex-wrap items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={saveEditingNotes}
                                              disabled={notesSaving}
                                              className="item-selected inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-sm font-semibold tracking-wider text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                              Save
                                            </button>
                                            <button
                                              type="button"
                                              onClick={cancelEditingNotes}
                                              disabled={notesSaving}
                                              className="item-selected inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-slate-400"
                                            >
                                              Cancel
                                            </button>
                                            {notesError && (
                                              <span className="text-xs text-red-500">
                                                {notesError}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-1">
                                          <span className="truncate">
                                            {session.notes || ''}
                                          </span>
                                          <button
                                            type="button"
                                            title="Edit notes"
                                            onClick={() =>
                                              startEditingNotes(session)
                                            }
                                            className="-mr-1 ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-slate-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200"
                                          >
                                            <span
                                              aria-hidden="true"
                                              className="text-sm leading-none"
                                            >
                                              ✏️
                                            </span>
                                            <span className="sr-only">
                                              Edit notes
                                            </span>
                                          </button>
                                        </div>
                                      )}
                                    </td>

                                    <td className="px-4 py-3 text-center align-top">
                                      <span
                                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                                          session.visibility === 'Public'
                                            ? 'border-green-200 bg-green-50 text-green-800'
                                            : 'border-red-200 bg-red-50 text-red-800'
                                        }`}
                                      >
                                        {session.visibility}
                                      </span>
                                    </td>

                                    {/* view column removed */}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="w-full pt-4">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate('/sessions')}
                            className="item-selected w-full"
                          >
                            View All Sessions
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="rounded-lg border border-dashed border-gray-300 bg-white/60 px-6 py-10 text-center text-sm text-gray-600">
                        No sessions recorded yet. Complete a session to see it
                        listed here.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Quick Stats */}
              <div className="card">
                <h2 className="gradient-text mb-4 pb-6 text-center text-4xl font-bold">
                  Your Stats
                </h2>
                <div className="space-y-4">
                  <div className="rounded-lg bg-primary-50 p-4 text-center">
                    <div className="text-2xl font-bold text-primary-700">
                      {formatHoursMinutes(totalSecondsForUser)}
                    </div>
                    <div className="text-sm text-gray-600">Total Time</div>
                  </div>
                  <div className="rounded-lg bg-secondary-50 p-4 text-center">
                    <div className="text-2xl font-bold text-secondary-700">
                      {dayStreak}
                    </div>
                    <div className="text-sm text-gray-600">Day Streak 🔥</div>
                  </div>
                </div>
                {!user && (
                  <div className="mt-4">
                    <div className="flex flex-col items-center gap-6 py-6 text-center">
                      <div>
                        <p className="mb-3 text-lg font-semibold text-gray-900">
                          Want to see your breathing stats?
                        </p>
                        <p className="text-gray-600">
                          Create a free account to save your sessions, follow
                          your progress, and join the{' '}
                          <span className="logo-font font-bold">Xhalin</span>{' '}
                          community.
                        </p>
                      </div>

                      <div className="w-full">
                        <Button
                          type="button"
                          variant="primary"
                          size="lg"
                          className="begin-breathing-button w-full"
                          onClick={() => navigate('/signup')}
                        >
                          Start My Journey
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {user && (
                  <div className="mt-6">
                    <div className="w-full pt-3">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate('/stats')}
                        className="item-selected w-full"
                      >
                        View All Stats
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Leaderboard */}
              <div className="card">
                <h2 className="gradient-text mb-4 pb-6 text-center text-4xl font-bold">
                  Global Leaderboard
                </h2>
                <div className="space-y-2">
                  <div className="mb-3 flex items-center justify-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={
                        leaderboardTab === 'minutes' ? 'primary' : 'secondary'
                      }
                      onClick={() => setLeaderboardTab('minutes')}
                      className={
                        leaderboardTab === 'minutes' ? 'item-selected' : ''
                      }
                    >
                      Top Minutes
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={
                        leaderboardTab === 'streak' ? 'primary' : 'secondary'
                      }
                      onClick={() => setLeaderboardTab('streak')}
                      className={
                        leaderboardTab === 'streak' ? 'item-selected' : ''
                      }
                    >
                      Top Day Streak
                    </Button>
                  </div>

                  {isLoadingLeaderboard ? (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-white/60 px-6 py-6 text-center text-sm text-gray-600">
                      Loading leaderboard…
                    </div>
                  ) : leaderboardError ? (
                    <div className="rounded-lg border border-dashed border-red-200 bg-red-50/80 px-6 py-6 text-center text-sm text-red-700">
                      {leaderboardError}
                    </div>
                  ) : displayedLeaderboard.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-white/60 px-6 py-6 text-center text-sm text-gray-600">
                      No leaderboard entries yet. Sessions shared to the
                      community will appear here.
                    </div>
                  ) : (
                    displayedLeaderboard.map((entry) => {
                      const isCurrentUser =
                        user?.id && entry.userId && entry.userId === user.id;
                      const profileLink = entry.profileSlug
                        ? `/profile/${encodeURIComponent(entry.profileSlug)}`
                        : entry.userId && user?.id && entry.userId === user.id
                          ? '/profile'
                          : null;
                      return (
                        <div
                          key={`${entry.userId ?? 'rank'}-${entry.rank}`}
                          className={`flex flex-col gap-3 rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between ${
                            isCurrentUser
                              ? 'bg-primary-50 font-semibold'
                              : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                            <span className="text-lg font-bold text-gray-500">
                              #{entry.rank}
                            </span>
                            <div className="flex items-center gap-3">
                              {entry.profilePictureUrl &&
                              entry.profilePictureUrl !==
                                DEFAULT_LEADERBOARD_AVATAR ? (
                                <img
                                  src={entry.profilePictureUrl}
                                  alt={entry.name}
                                  loading="lazy"
                                  className="h-10 w-10 rounded-full border border-gray-200 object-cover"
                                />
                              ) : (
                                <div
                                  className={`flex h-10 w-10 items-center justify-center rounded-full border-2 bg-white`}
                                >
                                  <div
                                    className={`flex h-9 w-9 items-center justify-center rounded-full ${
                                      entry.profileIsPrivate
                                        ? 'border-4 border-rose-600'
                                        : 'border-4 border-emerald-600'
                                    }`}
                                    aria-hidden="true"
                                  >
                                    <span className="text-2xl">😮‍💨</span>
                                  </div>
                                </div>
                              )}
                              <div>
                                {profileLink ? (
                                  <Link
                                    to={profileLink}
                                    className="text-sm font-medium text-primary-600 underline-offset-2 hover:underline focus-visible:underline"
                                  >
                                    {entry.name}
                                  </Link>
                                ) : (
                                  <div className="text-sm">{entry.name}</div>
                                )}
                                <div className="text-xs text-gray-500">
                                  {entry.sessions} sessions
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-end text-right text-sm font-semibold text-primary-600 sm:w-36 sm:justify-end">
                            {leaderboardTab === 'minutes'
                              ? formatHoursMinutes(
                                  entry.totalSeconds || entry.totalMinutes * 60
                                )
                              : `${entry.dayStreak ?? 0}d`}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div className="w-full pt-3">
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => navigate('/leaders')}
                      className="item-selected w-full"
                    >
                      View All Leaders
                    </Button>
                  </div>
                </div>
              </div>

              {/* Inspirational Quote (random active quote from DB) */}
              <div className="card bg-gradient-to-r from-primary-50 to-secondary-50">
                {activeQuote ? (
                  <>
                    <p className="italic text-gray-700">"{activeQuote.text}"</p>
                    {(activeQuote.author || '').trim() ? (
                      <p className="mt-2 text-right text-sm text-gray-600">
                        — {activeQuote.author?.trim()}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-gray-600">
                    No active quotes are available right now.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Full-screen breathing overlay */}
      {isFullMode && (
        <Container full fullBleed>
          <div className="flex h-full w-full flex-col items-center justify-center gap-8 text-center">
            <div className="mb-4">
              <h3 className="mb-2 text-3xl font-semibold text-white">
                {getPhaseLabel(currentPhase)}
              </h3>
              <p className="text-xl text-white/80">{phaseTimeLeft}s</p>
            </div>
            <div
              className={`breathe-circle h-72 w-72 bg-gradient-to-r from-primary-400 to-secondary-400 shadow-2xl ${
                currentPhase === 'inhale'
                  ? 'animate-breathe-in'
                  : currentPhase === 'exhale'
                    ? 'animate-breathe-out'
                    : currentPhase === 'hold-in'
                      ? 'scale-150'
                      : 'scale-100'
              }`}
            />
            <div className="mt-4 space-x-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={handleStopBreathing}
              >
                Stop Session
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => setIsFullMode(false)}
                className="border-white text-white hover:bg-white/10"
              >
                Minimize
              </Button>
            </div>
          </div>
        </Container>
      )}
    </>
  );
}

export default Dashboard;
